import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { X, Send, Loader, ChevronDown, Plus, Copy, Trash2, Check } from 'lucide-react';
import { WORK_TYPES, loadCalcData, getProductsByType, calculateMaterials, generateAIPrompt, generateWorkTypePrompt, generateFillFormPrompt } from './calcData';
import { supabase } from './supabaseClient';

// ═══════════════════════════════════════════════════════════════
// LIHTAR — Калькулятор матеріалів v3
// Дані з Supabase, кастомний dropdown, створення технологій
// ═══════════════════════════════════════════════════════════════

const BRAND = '#163248';
const ACCENT = '#f59e0b';

// Преселекти діаметрів отворів під сантехніку
const HOLE_PRESETS = [
  { id: 'socket',    label: 'Розетка / вимикач',      diameter: '68'  },
  { id: 'pipe_half', label: 'Труба водопостачання ½"', diameter: '35'  },
  { id: 'pipe_3q',   label: 'Труба водопостачання ¾"', diameter: '40'  },
  { id: 'sewer_50',  label: 'Каналізація 50мм',        diameter: '63'  },
  { id: 'sewer_110', label: 'Каналізація 110мм',       diameter: '127' },
  { id: 'other',     label: 'Інший діаметр',           diameter: ''    },
];

// ─── Session persistence ───
const STORAGE_KEY = 'lihtar_calc_v3';
const saveState = (state) => { try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch (e) { /* */ } };
const loadState = () => { try { const s = sessionStorage.getItem(STORAGE_KEY); return s ? JSON.parse(s) : null; } catch (e) { return null; } };
const clearState = () => { try { sessionStorage.removeItem(STORAGE_KEY); } catch (e) { /* */ } };

// ─── Кнопка "Назад" — стрілка в кружку ───
const BackBtn = ({ onClick }) => (
  <button onClick={onClick} style={{
    width: 32, height: 32, borderRadius: '50%', border: `1.5px solid ${BRAND}`,
    background: 'transparent', cursor: 'pointer', display: 'flex',
    alignItems: 'center', justifyContent: 'center', flexShrink: 0, padding: 0,
  }}>
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
      <path d="M9 2L4.5 7L9 12" stroke={BRAND} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  </button>
);

// ─── Секція що згортається ───
const Section = ({ title, count, children, defaultOpen = true, hint }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ border: '1px solid #e5e7eb', borderRadius: 12, overflow: 'hidden', marginBottom: 10 }}>
      <button onClick={() => setOpen(!open)} style={{
        width: '100%', padding: '10px 16px', display: 'flex', alignItems: 'center',
        justifyContent: 'space-between', background: '#fafaf9', border: 'none',
        cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left',
      }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: BRAND }}>
          {title}
          {count != null && <span style={{ fontWeight: 400, color: '#9ca3af', marginLeft: 6, fontSize: 12 }}>{count}</span>}
        </span>
        <span style={{ fontSize: 12, color: '#9ca3af', transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform .2s', display: 'inline-block' }}>▾</span>
      </button>
      {open && (
        <div style={{ borderTop: '1px solid #f3f4f6' }}>
          {children}
          {hint && <div style={{ padding: '6px 16px 10px', fontSize: 11, color: '#9ca3af' }}>{hint}</div>}
        </div>
      )}
    </div>
  );
};

// ─── Кастомний Dropdown (fixed position — не обрізається overflow) ───
const CustomSelect = ({ value, options, onChange, disabled }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const btnRef = useRef(null);
  const [dropStyle, setDropStyle] = useState({});
  const selected = options.find(o => o.id === value) || options[0];

  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target) && btnRef.current && !btnRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  useEffect(() => {
    if (!open || !btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const dropH = Math.min((options.length) * 40, 200);
    const openUp = spaceBelow < dropH + 8 && rect.top > dropH + 8;
    setDropStyle({
      position: 'fixed',
      left: rect.left,
      width: rect.width,
      ...(openUp
        ? { bottom: window.innerHeight - rect.top + 4 }
        : { top: rect.bottom + 4 }),
      zIndex: 9999,
    });
  }, [open, options.length]);

  if (!options || options.length <= 1) {
    return (
      <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 3 }}>
        {selected?.name}{selected?.manufacturer && selected.manufacturer !== '-' ? ` · ${selected.manufacturer}` : ''}
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', marginTop: 4 }}>
      <button
        ref={btnRef}
        onClick={() => !disabled && setOpen(!open)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '7px 10px', border: `1.5px solid ${open ? BRAND : '#e5e7eb'}`,
          borderRadius: 10, background: '#fafaf9', cursor: disabled ? 'default' : 'pointer',
          fontFamily: 'inherit', fontSize: 12, color: '#374151', textAlign: 'left',
          transition: 'border-color .15s',
        }}
      >
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {selected?.name}{selected?.manufacturer && selected.manufacturer !== '-' ? ` · ${selected.manufacturer}` : ''}
        </span>
        <ChevronDown style={{
          width: 14, height: 14, color: '#9ca3af', flexShrink: 0, marginLeft: 4,
          transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform .15s',
        }} />
      </button>

      {open && ReactDOM.createPortal(
        <div ref={ref} style={{
          ...dropStyle,
          background: '#fff', border: '1.5px solid #e5e7eb', borderRadius: 12,
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          overflow: 'hidden',
        }}>
          <div style={{ maxHeight: 200, overflowY: 'auto' }}>
          {options.map((opt, i) => (
            <button
              key={opt.id}
              onClick={() => { onChange(opt.id); setOpen(false); }}
              style={{
                width: '100%', padding: '10px 12px', border: 'none',
                background: opt.id === value ? '#f0f4f8' : '#fff',
                cursor: 'pointer', fontFamily: 'inherit', fontSize: 12,
                color: opt.id === value ? BRAND : '#374151',
                fontWeight: opt.id === value ? 600 : 400,
                textAlign: 'left', display: 'block',
                borderBottom: i < options.length - 1 ? '1px solid #f3f4f6' : 'none',
                transition: 'background .1s',
              }}
              onMouseEnter={e => { if (opt.id !== value) e.target.style.background = '#fafaf9'; }}
              onMouseLeave={e => { if (opt.id !== value) e.target.style.background = '#fff'; }}
            >
              {opt.name}{opt.manufacturer && opt.manufacturer !== '-' ? ` · ${opt.manufacturer}` : ''}
            </button>
          ))}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

// ─── Бібліотека шарів для конструктора ───
const LAYER_LIBRARY = [
  { id: 'primer', name: 'Грунтовка', productType: 'primer', hasThickness: false, calcType: 'area' },
  { id: 'waterproof', name: 'Гідроізоляція', productType: 'waterproof', hasThickness: false, calcType: 'area' },
  { id: 'waterproof_tape', name: 'Стрічка гідроізоляції', productType: 'waterproof_tape', hasThickness: false, calcType: 'perimeter' },
  { id: 'damper', name: 'Демпферна стрічка', productType: 'damper_tape', hasThickness: false, calcType: 'perimeter' },
  { id: 'epps', name: 'EPPС (утеплювач)', productType: 'epps', hasThickness: true, calcType: 'area', defaultThickness: 50 },
  { id: 'insulation_glue', name: 'Клей для утеплювача', productType: 'insulation_glue', hasThickness: false, calcType: 'area' },
  { id: 'bobbin_mat', name: 'Мат з бобишками', productType: 'bobbin_mat', hasThickness: false, calcType: 'area' },
  { id: 'film', name: 'ПЕ плівка', productType: 'pe_film', hasThickness: false, calcType: 'area' },
  { id: 'roll_waterproof', name: 'Рулонна гідроізоляція', productType: 'roll_waterproof', hasThickness: false, calcType: 'area' },
  { id: 'reflective_film', name: 'Відбиваюча плівка', productType: 'reflective_film', hasThickness: false, calcType: 'area' },
  { id: 'mesh', name: 'Армуюча сітка', productType: 'mesh', hasThickness: false, calcType: 'area' },
  { id: 'beacons', name: 'Маяки', productType: 'beacons', hasThickness: false, calcType: 'special' },
  { id: 'screed', name: 'ЦПС стяжка', productType: 'screed_mix', hasThickness: true, calcType: 'volume', defaultThickness: 50 },
  { id: 'semi_dry', name: 'Напівсуха стяжка', productType: 'semi_dry_mix', hasThickness: true, calcType: 'volume', defaultThickness: 60 },
  { id: 'self_leveling', name: 'Самовирівнювальна суміш', productType: 'self_leveling_mix', hasThickness: true, calcType: 'volume', defaultThickness: 10 },
  { id: 'light_screed', name: 'Легка стяжка', productType: 'light_screed_mix', hasThickness: true, calcType: 'volume', defaultThickness: 40 },
  // Штукатурка
  { id: 'primer_plaster', name: 'Грунтовка', productType: 'primer_plaster', hasThickness: false, calcType: 'area' },
  { id: 'primer_gasblock', name: 'Грунтовка для газобетону', productType: 'primer_gasblock', hasThickness: false, calcType: 'area' },
  { id: 'betoncontact', name: 'Бетонконтакт', productType: 'betoncontact', hasThickness: false, calcType: 'area' },
  { id: 'plaster_mesh', name: 'Штукатурна сітка', productType: 'plaster_mesh', hasThickness: false, calcType: 'area' },
  { id: 'plaster_beacon', name: 'Маяки 6мм 3м', productType: 'plaster_beacon', hasThickness: false, calcType: 'special' },
  { id: 'plaster_gypsum', name: 'Штукатурка гіпсова', productType: 'plaster_gypsum_mix', hasThickness: true, calcType: 'area', defaultThickness: 15 },
  { id: 'plaster_cement', name: 'Штукатурка цементна', productType: 'plaster_cement_mix', hasThickness: true, calcType: 'area', defaultThickness: 15 },
  { id: 'plaster_gasblock', name: 'Штукатурка для газобетону', productType: 'plaster_gasblock_mix', hasThickness: true, calcType: 'area', defaultThickness: 10 },
  { id: 'plaster_machine_gypsum', name: 'Штукатурка машинна гіпсова', productType: 'plaster_machine_gypsum', hasThickness: true, calcType: 'area', defaultThickness: 15 },
  { id: 'plaster_machine_cement', name: 'Штукатурка машинна цементна', productType: 'plaster_machine_cement', hasThickness: true, calcType: 'area', defaultThickness: 15 },
  { id: 'plaster_cement_lime', name: 'Штукатурка цементно-вапняна', productType: 'plaster_cement_lime_mix', hasThickness: true, calcType: 'area', defaultThickness: 15 },
  // Підготовка стін (шпаклювання)
  { id: 'primer_wall_prep', name: 'Грунтовка', productType: 'primer_wall_prep', hasThickness: false, calcType: 'area' },
  { id: 'putty_start_gypsum', name: 'Шпаклівка стартова гіпсова', productType: 'putty_start_gypsum', hasThickness: true, calcType: 'area', defaultThickness: 3 },
  { id: 'putty_finish_gypsum', name: 'Шпаклівка фінішна гіпсова', productType: 'putty_finish_gypsum', hasThickness: true, calcType: 'area', defaultThickness: 1.5 },
  { id: 'putty_finish_polymer', name: 'Шпаклівка фінішна полімерна', productType: 'putty_finish_polymer', hasThickness: true, calcType: 'area', defaultThickness: 1 },
  { id: 'putty_joint', name: 'Шпаклівка для швів ГКЛ', productType: 'putty_joint', hasThickness: false, calcType: 'special_drywall' },
  { id: 'serpyanka', name: 'Серп\'янка для стиків', productType: 'serpyanka', hasThickness: false, calcType: 'special_drywall' },
  { id: 'paper_tape', name: 'Паперова стрічка для кутів', productType: 'paper_tape', hasThickness: false, calcType: 'area' },
  { id: 'corner_profile', name: 'Перфорований кутник 3м', productType: 'corner_profile', hasThickness: false, calcType: 'special' },
  { id: 'wp_fiberglass_mesh', name: 'Склосітка 5×5 160г/м²', productType: 'wp_fiberglass_mesh', hasThickness: false, calcType: 'area' },
  { id: 'glassveil', name: 'Склохолст / флізелін', productType: 'glassveil', hasThickness: false, calcType: 'area' },
  { id: 'glassveil_glue', name: 'Клей для склохолсту', productType: 'glassveil_glue', hasThickness: false, calcType: 'area' },
  // Перегородки ГКЛ
  { id: 'profile_uw', name: 'Профіль UW (направляючий)', productType: 'auto_uw', hasThickness: false, calcType: 'partition_uw' },
  { id: 'profile_cw', name: 'Профіль CW (стоєчний)', productType: 'auto_cw', hasThickness: false, calcType: 'partition_cw' },
  { id: 'gkl', name: 'ГКЛ 12.5мм', productType: 'auto_gkl', hasThickness: false, calcType: 'partition_gkl' },
  { id: 'timber_guide', name: 'Брус направляючий 60×40', productType: 'timber_guide', hasThickness: false, calcType: 'partition_uw' },
  { id: 'timber_stud', name: 'Брус стоєчний 60×50', productType: 'timber_stud', hasThickness: false, calcType: 'partition_cw' },
];

// ═══ ГОЛОВНИЙ КОМПОНЕНТ ═══
const MaterialCalculator = ({ isOpen, onClose, user, onCreateOrder, isGuest = false, onGuestRegister }) => {
  const saved = loadState();
  // Захист від старого кешу без нових полів
  if (saved?.dims) {
    if (!saved.dims.niches) saved.dims.niches = [];
    if (saved.dims.ceilArea === undefined) saved.dims.ceilArea = '';
    if (saved.dims.ceilPerimeter === undefined) saved.dims.ceilPerimeter = '';
  }

  const [step, setStep] = useState(saved?.step || 'work_types');
  const [selectedWork, setSelectedWork] = useState(saved?.selectedWork || null);
  const [activeTags, setActiveTags] = useState(saved?.activeTags || []);
  const [selectedTechId, setSelectedTechId] = useState(saved?.selectedTechId || null);
  const [expandedCard, setExpandedCard] = useState(saved?.expandedCard || null);
  const DIMS_DEFAULTS = { length: '', width: '', height: '', wallLength: '', doorOpenings: [], windowOpenings: [], innerCorners: '', outerCorners: '', openingDepth: '', hasGrooves: false, grooveLength: '', ceilingJoint: false, sandingType: 'manual', partitionLength: '', ptWidth: '75', useOsb: false, useInsulation: false, insulationThickness: '50', profileStep: '0.6', columnA: '', columnB: '', boxLength: '', gcDepth: '', gcWidth: '', nicheSides: '3', shelfCount: '1', shelfLength: '', shelfDepth: '', ceilDrop: '', rebarEvery: '3', mortarMode: 'ready', lintelMode: 'aeroc', ceilArea: '', ceilPerimeter: '', niches: [] };
  const [dims, setDims] = useState(saved?.dims || DIMS_DEFAULTS);
  const [layerThicknesses, setLayerThicknesses] = useState(saved?.layerThicknesses || {});
  const [layerToggles, setLayerToggles] = useState(saved?.layerToggles || {});
  const [productSelections, setProductSelections] = useState(saved?.productSelections || {});
  const [expandedSections, setExpandedSections] = useState(saved?.expandedSections || { materials: true, consumables: true, tools: false });

  // AI Chat
  const [chatInput, setChatInput] = useState('');
  const [chatMessages, setChatMessages] = useState(saved?.chatMessages || []);
  const [chatLoading, setChatLoading] = useState(false);
  const chatEndRef = React.useRef(null);
  React.useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [chatMessages, chatLoading]);
  const [aiSuggestions, setAiSuggestions] = useState(saved?.aiSuggestions || []);

  // Create Technology
  const [showCreateMode, setShowCreateMode] = useState(false);
  const [customTechName, setCustomTechName] = useState('');
  const [customLayers, setCustomLayers] = useState([]);
  const [showLayerLibrary, setShowLayerLibrary] = useState(false);

  // Custom technology (built or copied)
  const [customTech, setCustomTech] = useState(saved?.customTech || null);

  // ─── Supabase data ───
  const [dbTags, setDbTags] = useState([]);
  const [dbTechnologies, setDbTechnologies] = useState([]);
  const [dataLoading, setDataLoading] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);

  // ─── Pipe params (водяна ТП) ───
  const [pipeParams, setPipeParams] = useState(saved?.pipeParams || { diameter: '16', step: '150' });

  // ─── Consumable edits ───
  const [consumableEdits, setConsumableEdits] = useState(saved?.consumableEdits || {}); // { index: newQty }
  const [deletedConsumables, setDeletedConsumables] = useState(saved?.deletedConsumables || []); // [index, ...]
  const [customConsumables, setCustomConsumables] = useState(saved?.customConsumables || []); // [{ name, qty, unit }, ...]
  const [showAddConsumable, setShowAddConsumable] = useState(false);
  const [newConsumableName, setNewConsumableName] = useState('');
  const [newConsumableQty, setNewConsumableQty] = useState('');
  const [newConsumableUnit, setNewConsumableUnit] = useState('шт.');

  const contentRef = useRef(null);

  // ─── Load data from Supabase ───
  const loadData = useCallback(async (workType) => {
    if (!workType) return;
    setDataLoading(true);
    try {
      const { tags, technologies } = await loadCalcData(workType);
      setDbTags(tags);
      setDbTechnologies(technologies);
      setDataLoaded(true);
      // Для плиточних робіт — автоматично вибираємо єдину технологію
      if (workType === 'tiling' && technologies.length > 0) {
        const tech = technologies[0];
        setSelectedTechId(tech.id);
        setDims(d => ({ ...d, tilingArea: '', tilingType: 'standard', tilingWarmFloor: false, hydroArea: '', outerCorners: '', innerCorners: '', tileW: '', tileH: '', groutWidth: '', svpMode: 'crosses', holes: [] }));
        setLayerThicknesses({});
        setProductSelections({});
        setConsumableEdits({}); setDeletedConsumables([]); setCustomConsumables([]);
        setStep('calculate');
      }
    } catch (err) {
      console.error('loadCalcData:', err);
    }
    setDataLoading(false);
  }, []);

  useEffect(() => {
    if (selectedWork && !dataLoaded) loadData(selectedWork);
  }, [selectedWork, dataLoaded, loadData]);

  // ─── Derive selectedTech ───
  const selectedTech = useMemo(() => {
    if (customTech && selectedTechId === '__custom__') return customTech;
    if (!selectedTechId || !selectedWork) return null;
    return dbTechnologies.find(t => t.id === selectedTechId) || null;
  }, [selectedTechId, selectedWork, customTech, dbTechnologies]);

  const filteredTechs = useMemo(() => {
    if (!selectedWork) return [];
    if (activeTags.length === 0) return dbTechnologies;
    return dbTechnologies.filter(t => activeTags.every(tag => t.tags.includes(tag)));
  }, [selectedWork, activeTags, dbTechnologies]);

  const tags = dbTags;

  // ─── Warm water floor detection ───
  const isWarmWater = useMemo(() => {
    return selectedTech && (selectedTech.tags || []).includes('warm_floor_water');
  }, [selectedTech]);

  // ─── Save state ───
  useEffect(() => {
    saveState({
      step, selectedWork, activeTags, selectedTechId, expandedCard,
      dims, layerThicknesses, layerToggles, productSelections,
      expandedSections, chatMessages, aiSuggestions, customTech, pipeParams,
      consumableEdits, deletedConsumables, customConsumables,
    });
  }, [step, selectedWork, activeTags, selectedTechId, expandedCard, dims, layerThicknesses, layerToggles, productSelections, expandedSections, chatMessages, aiSuggestions, customTech, pipeParams, consumableEdits, deletedConsumables, customConsumables]);

  useEffect(() => {
    if (isOpen) { document.body.style.overflow = 'hidden'; return () => { document.body.style.overflow = ''; }; }
  }, [isOpen]);

  useEffect(() => { contentRef.current?.scrollTo(0, 0); }, [step]);

  const isPlaster = selectedWork === 'plaster' || selectedWork === 'wall_prep';
  const isCeilingPrep = selectedWork === 'ceiling_prep';
  const isPartition = selectedWork === 'partition';
  const isCladding = selectedWork === 'cladding';
  const isConstruct = selectedWork === 'gkl_construct';
  const isCeiling = selectedWork === 'ceiling';
  const isMasonry = selectedWork === 'masonry';
  const isTiling = selectedWork === 'tiling';

  // Автопідбір клею для плитки: тільки тепла підлога → elastic примусово
  // Тип плитки (мозаїка/керамограніт) впливає лише на витрату, НЕ на тип клею
  // Білий клей (cm117w) доступний для вибору вручну для будь-якого типу плитки
  useEffect(() => {
    if (!isTiling) return;
    const isWarm = !!dims.tilingWarmFloor;
    if (isWarm) {
      // Тепла підлога → примусово elastic
      setProductSelections(prev => ({ ...prev, adhesive: 'tile_adh_el_ceresit_cm117' }));
    } else {
      // Знята тепла підлога: якщо клей був elastic — повертаємо стандарт
      setProductSelections(prev => {
        const cur = prev.adhesive || '';
        if (cur.includes('_el_')) return { ...prev, adhesive: 'tile_adh_ceresit_cm11' };
        return prev;
      });
    }
  }, [dims.tilingWarmFloor, isTiling]);

  // ─── Calculate ───
  const result = useMemo(() => {
    if (isTiling) {
      if (!selectedTech || !dims.tilingArea) return null;
      return calculateMaterials(selectedTech, dims, productSelections, layerThicknesses, layerToggles, pipeParams, 'tiling');
    }
    if (isMasonry) {
      if (!selectedTech || !dims.height || !dims.wallLength) return null;
      return calculateMaterials(selectedTech, dims, productSelections, layerThicknesses, layerToggles, pipeParams, 'masonry');
    }
    if (isCeiling) {
      if (!selectedTech || !dims.length || !dims.width) return null;
      return calculateMaterials(selectedTech, dims, productSelections, layerThicknesses, layerToggles, pipeParams, 'ceiling');
    }
    if (isConstruct) {
      if (!selectedTech) return null;
      const tags = selectedTech.tags || [];
      if (tags.includes('gc_column') && (!dims.columnA || !dims.columnB || !dims.height)) return null;
      if ((tags.includes('gc_box_p') || tags.includes('gc_box_g')) && (!dims.columnA || !dims.columnB || !dims.boxLength)) return null;
      if (tags.includes('gc_install') && (!dims.gcWidth || !dims.gcDepth || !dims.height)) return null;
      if (tags.includes('gc_box_h') && (!dims.gcWidth || !dims.height || !dims.gcDepth)) return null;
      if (tags.includes('gc_niche') && (!dims.gcWidth || !dims.height || !dims.gcDepth)) return null;
      if (tags.includes('gc_shelf') && (!dims.shelfLength || !dims.shelfDepth)) return null;
      return calculateMaterials(selectedTech, dims, productSelections, layerThicknesses, layerToggles, pipeParams, 'gkl_construct');
    }
    if (isPartition) {
      if (!selectedTech || !dims.height || !dims.partitionLength) return null;
      return calculateMaterials(selectedTech, dims, productSelections, layerThicknesses, layerToggles, pipeParams, 'partition');
    }
    if (isCladding) {
      if (!selectedTech || !dims.height || !dims.wallLength) return null;
      return calculateMaterials(selectedTech, dims, productSelections, layerThicknesses, layerToggles, pipeParams, 'cladding');
    }
    if (isCeilingPrep) {
      return calculateMaterials(selectedTech, dims, productSelections, layerThicknesses, layerToggles, pipeParams, 'ceiling_prep');
    }
    if (isPlaster) {
      if (!selectedTech || !dims.height || !dims.wallLength) return null;
      return calculateMaterials(selectedTech, dims, productSelections, layerThicknesses, layerToggles, pipeParams, selectedWork);
    }
    if (!selectedTech || !dims.length || !dims.width) return null;
    return calculateMaterials(selectedTech, dims, productSelections, layerThicknesses, layerToggles, pipeParams, 'screed');
  }, [selectedTech, dims, productSelections, layerThicknesses, layerToggles, pipeParams, isPlaster, isPartition, isTiling, isCeilingPrep, selectedWork]);

  // ─── Navigation ───
  const selectWork = (id) => {
    setSelectedWork(id); setActiveTags([]); setExpandedCard(null);
    setAiSuggestions([]); setChatMessages([]); setShowCreateMode(false);
    setDataLoaded(false);
    setStep('technologies');
  };

  const selectTech = (tech) => {
    setSelectedTechId(tech.id);
    setDims(DIMS_DEFAULTS);
    const thk = {};
    tech.layers.forEach(l => { if (l.hasThickness) thk[l.id] = String(l.defaultThickness || 50); });
    setLayerThicknesses(thk);
    setProductSelections({});
    setConsumableEdits({}); setDeletedConsumables([]); setCustomConsumables([]);
    setStep('calculate');
  };

  const goBack = () => {
    if (step === 'calculate') {
      if (isTiling) { setStep('work_types'); setSelectedWork(null); setSelectedTechId(null); setDataLoaded(false); }
      else { setStep('technologies'); setSelectedTechId(null); setCustomTech(null); }
    }
    else if (step === 'technologies') { setStep('work_types'); setSelectedWork(null); setActiveTags([]); setAiSuggestions([]); setChatMessages([]); setShowCreateMode(false); setDataLoaded(false); }
  };

  const restart = () => {
    clearState();
    setStep('work_types'); setSelectedWork(null); setActiveTags([]);
    setSelectedTechId(null); setExpandedCard(null);
    setDims(DIMS_DEFAULTS); setLayerThicknesses({});
    setProductSelections({}); setChatMessages([]); setAiSuggestions([]);
    setShowCreateMode(false); setCustomTech(null); setCustomLayers([]);
    setCustomTechName(''); setDataLoaded(false);
    setPipeParams({ diameter: '16', step: '150' });
    setConsumableEdits({}); setDeletedConsumables([]); setCustomConsumables([]);
    setShowAddConsumable(false);
  };

  const changeProduct = useCallback((layerId, productId) => {
    setProductSelections(prev => ({ ...prev, [layerId]: productId }));
    // Автовизначення ширини профілю при виборі UW
    if (layerId === 'uw' && productId && isPartition) {
      const w = productId.includes('uw50') ? '50' : productId.includes('uw75') ? '75' : productId.includes('uw100') ? '100' : null;
      if (w) setDims(d => ({ ...d, ptWidth: w }));
    }
  }, [isPartition]);

  const fmt = (n) => Math.ceil(n).toLocaleString('uk-UA');

  // ─── Create Technology ───
  const startFromScratch = () => {
    setCustomTechName('');
    setCustomLayers([]);
    setShowCreateMode(true);
    setShowLayerLibrary(false);
  };

  const copyTech = (tech) => {
    setCustomTechName(tech.name + ' (копія)');
    setCustomLayers(tech.layers.map(l => ({
      ...l,
      _uid: l.id + '_' + Date.now() + '_' + Math.random().toString(36).slice(2, 6),
    })));
    setShowCreateMode(true);
    setShowLayerLibrary(false);
    setExpandedCard(null);
  };

  const addLayerFromLibrary = (libLayer) => {
    setCustomLayers(prev => [...prev, {
      ...libLayer,
      _uid: libLayer.id + '_' + Date.now() + '_' + Math.random().toString(36).slice(2, 6),
      required: true, enabled: true,
    }]);
    setShowLayerLibrary(false);
  };

  const removeCustomLayer = (uid) => {
    setCustomLayers(prev => prev.filter(l => l._uid !== uid));
  };

  const useCustomTech = () => {
    if (!customTechName.trim() || customLayers.length === 0) return;
    const tech = {
      id: '__custom__',
      name: customTechName.trim(),
      description: 'Користувацька технологія',
      tags: [],
      author: 'Ви',
      brand: 'Ви',
      verified: false,
      layers: customLayers.map(l => ({
        id: l.id,
        name: l.name,
        productType: l.productType,
        hasThickness: l.hasThickness,
        defaultThickness: l.defaultThickness || 50,
        calcType: l.calcType,
        required: true,
        enabled: true,
      })),
      consumables: [],
      tools: [],
      notes: '',
    };
    setCustomTech(tech);
    selectTech(tech);
    setShowCreateMode(false);
  };

  // ─── AI Chat ───
  const callAI = async (systemPrompt, messages, token) => {
    const response = await fetch('https://jkouufahpliqgyvcmbwb.supabase.co/functions/v1/ai-calculator', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ system: systemPrompt, messages }),
    });
    const data = await response.json();
    return data.text || '';
  };

  const parseAIResponse = (aiText, preferObject = false) => {
    // Прибираємо markdown обгортку
    const clean = aiText.replace(/```json\s*/gi, '').replace(/```\s*/gi, '').trim();
    const textOutside = clean.replace(/\[[\s\S]*\]/, '').replace(/\{[\s\S]*\}/, '').trim();

    const arrStart = clean.indexOf('[');
    const arrEnd = clean.lastIndexOf(']');
    const objStart = clean.indexOf('{');
    const objEnd = clean.lastIndexOf('}');

    // preferObject=true (calculate): спочатку шукаємо об'єкт {}, потім масив []
    if (preferObject && objStart !== -1 && objEnd > objStart) {
      try { return { parsed: JSON.parse(clean.slice(objStart, objEnd + 1)), text: textOutside }; } catch {}
    }
    if (arrStart !== -1 && arrEnd > arrStart) {
      try { return { parsed: JSON.parse(clean.slice(arrStart, arrEnd + 1)), text: textOutside }; } catch {}
    }
    if (objStart !== -1 && objEnd > objStart) {
      try { return { parsed: JSON.parse(clean.slice(objStart, objEnd + 1)), text: textOutside }; } catch {}
    }

    return { parsed: null, text: aiText };
  };

  const sendChat = async () => {
    const text = chatInput.trim();
    if (!text || chatLoading) return;
    const newMessages = [...chatMessages, { role: 'user', text }];
    setChatMessages(newMessages);
    setChatInput('');
    setChatLoading(true);
    setAiSuggestions([]);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) throw new Error('Не авторизовано');
      const msgs = newMessages.map(m => ({ role: m.role === 'user' ? 'user' : 'assistant', content: m.text }));

      // ── Режим 1: work_types — підбір виду робіт ──
      if (step === 'work_types') {
        const aiText = await callAI(generateWorkTypePrompt(), msgs, token);
        const { parsed, text: textOut } = parseAIResponse(aiText);
        if (parsed && Array.isArray(parsed) && parsed.length > 0 && parsed[0].workId) {
          setAiSuggestions(parsed.map(s => ({ ...s, isWorkType: true })));
          setChatMessages(prev => [...prev, { role: 'assistant', text: textOut || 'Ось що підходить для вашої задачі:' }]);
        } else {
          setChatMessages(prev => [...prev, { role: 'assistant', text: aiText }]);
        }

      // ── Режим 2: technologies — підбір технології ──
      } else if (step === 'technologies') {
        const aiText = await callAI(generateAIPrompt(dbTechnologies, selectedWork), msgs, token);
        const { parsed, text: textOut } = parseAIResponse(aiText);
        if (parsed && Array.isArray(parsed) && parsed.length > 0 && parsed[0].techId) {
          setAiSuggestions(parsed);
          setChatMessages(prev => [...prev, { role: 'assistant', text: textOut || 'Ось що я рекомендую:' }]);
        } else {
          setChatMessages(prev => [...prev, { role: 'assistant', text: aiText }]);
        }

      // ── Режим 3: calculate — заповнення форми ──
      } else if (step === 'calculate') {
        const aiText = await callAI(generateFillFormPrompt(selectedWork, dims), msgs, token);
        const { parsed, text: textOut } = parseAIResponse(aiText, true);
        if (parsed && !Array.isArray(parsed) && typeof parsed === 'object' && Object.keys(parsed).length > 0) {
          setDims(d => ({ ...d, ...parsed }));
          setChatMessages(prev => [...prev, { role: 'assistant', text: 'Параметри заповнені ✓ Перевірте і уточніть якщо потрібно.' }]);
        } else {
          setChatMessages(prev => [...prev, { role: 'assistant', text: aiText }]);
        }
      }
    } catch (err) {
      setChatMessages(prev => [...prev, { role: 'assistant', text: "Помилка з'єднання. Спробуйте ще раз." }]);
    }
    setChatLoading(false);
  };

  // ─── Create Order ───
  const [orderCreating, setOrderCreating] = useState(false);
  const [orderCreated, setOrderCreated] = useState(false);

  const createOrder = async () => {
    if (!result || !user || orderCreating) return;
    setOrderCreating(true);
    try {
      // Відмінювання одиниць (підтримує і скорочення і повні форми)
      const pluralize = (n, unit) => {
        const num = Math.abs(parseFloat(n)) || 0;
        const last1 = num % 10;
        const last2 = num % 100;
        const forms = {
          'міш.': ['мішок', 'мішки', 'мішків'],
          'мішок': ['мішок', 'мішки', 'мішків'],
          'каніс.': ['каністра', 'каністри', 'каністр'],
          'каністра': ['каністра', 'каністри', 'каністр'],
          'рул.': ['рулон', 'рулони', 'рулонів'],
          'рулон': ['рулон', 'рулони', 'рулонів'],
          'карта': ['карта', 'карти', 'карт'],
          'уп.': ['упаковка', 'упаковки', 'упаковок'],
          'упаковка': ['упаковка', 'упаковки', 'упаковок'],
        };
        if (!forms[unit]) return unit;
        const [one, few, many] = forms[unit];
        if (last2 >= 11 && last2 <= 19) return many;
        if (last1 === 1) return one;
        if (last1 >= 2 && last1 <= 4) return few;
        return many;
      };

      // Збираємо основні матеріали (в одиницях пакування + detail)
      const layerMaterials = result.layers.map(layer => {
        // Назва: тільки назва продукту
        const productName = layer.product ? layer.product.name : layer.name;
        // Формуємо detail — текст розрахунку з відмінюванням
        let detail = '';
        if (layer.packs > 0 && layer.packagingUnit && layer.packagingSize) {
          const packWord = pluralize(layer.packs, layer.packagingUnit);
          detail = `${layer.qtyRounded} ${layer.unit} → ${layer.packs} ${packWord} × ${layer.packagingSize} ${layer.unit}`;
          return { name: productName, qty: layer.packs, unit: layer.packagingUnit, detail };
        }
        if (layer.thickness) detail = `товщина ${layer.thickness} мм`;
        return { name: productName, qty: layer.qtyRounded, unit: layer.unit || 'шт.', detail };
      });

      // Збираємо витратні матеріали (з урахуванням редагувань і видалень)
      const consumableMaterials = result.consumables
        .map((c, i) => {
          if (deletedConsumables.includes(i)) return null;
          const qty = consumableEdits[i] !== undefined ? (parseInt(consumableEdits[i]) || 0) : c.qty;
          return { name: c.name, qty, unit: c.unit, detail: '' };
        })
        .filter(Boolean);

      // Додаємо користувацькі витратні
      const customMats = customConsumables.map(cc => ({
        name: cc.name, qty: parseInt(cc.qty) || 0, unit: cc.unit, detail: '',
      }));

      const allMaterials = [...layerMaterials, ...consumableMaterials, ...customMats].filter(m => m.qty > 0);

      const techName = selectedTech?.name || 'Калькулятор';
      const workName = WORK_TYPES.find(w => w.id === selectedWork)?.name || '';
      const orderName = `${workName} — ${techName} (${result.area} м²)`;

      const { data: orderData, error } = await supabase
        .from('order_cards')
        .insert({
          user_id: user.id,
          name: orderName,
          materials: allMaterials,
          source_template_id: null,
          contractor_name: null,
        })
        .select()
        .single();

      if (error) throw error;

      if (orderData && onCreateOrder) {
        onCreateOrder(orderData);
      }

      setOrderCreated(true);
      setTimeout(() => setOrderCreated(false), 2000);

      if (window.showToast) window.showToast('Замовлення створено!');
    } catch (err) {
      console.error('createOrder:', err);
      if (window.showToast) window.showToast('Помилка створення замовлення');
    }
    setOrderCreating(false);
  };

  const inputStyle = {
    width: '100%', padding: '10px 12px', border: '1.5px solid #e5e7eb',
    borderRadius: 10, fontSize: 15, fontWeight: 500, outline: 'none',
    fontFamily: 'inherit', background: '#fff', boxSizing: 'border-box',
  };

  if (!isOpen) return null;

  return (
    <div
      className={`fixed flex items-center justify-center`}
      style={{ background: isGuest ? 'rgba(0,0,0,0.45)' : 'rgba(0,0,0,0.45)', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999 }}
      onClick={(e) => { if (e.target === e.currentTarget && !isGuest) { restart(); onClose(); } }}
    >
      <div
        className="bg-white w-full sm:max-w-xl sm:rounded-2xl rounded-t-2xl shadow-2xl overflow-hidden flex flex-col"
        style={{ maxHeight: '92vh' }}
        onClick={e => e.stopPropagation()}
      >
        {/* ═══ HEADER ═══ */}
        <div className="px-4 py-3 flex items-center gap-2.5 border-b flex-shrink-0" style={{ borderColor: '#e5e7eb' }}>
          {step !== 'work_types' && <BackBtn onClick={goBack} />}
          <div className="flex-1">
            <div className="text-sm font-bold" style={{ color: BRAND }}>Калькулятор матеріалів</div>
            <div className="text-[11px] mt-0.5" style={{ color: '#9ca3af' }}>
              {step === 'work_types' && 'Оберіть вид робіт'}
              {step === 'technologies' && (showCreateMode ? 'Створення технології' : WORK_TYPES.find(w => w.id === selectedWork)?.name)}
              {step === 'calculate' && selectedTech?.name}
            </div>
          </div>
          <button onClick={() => { restart(); onClose(); }} style={{
            width: 32, height: 32, borderRadius: '50%', border: '1.5px solid #e5e7eb',
            background: 'transparent', cursor: 'pointer', display: 'flex',
            alignItems: 'center', justifyContent: 'center', fontSize: 14, color: '#9ca3af', padding: 0,
          }}>✕</button>
        </div>

        {/* ═══ CONTENT ═══ */}
        <div ref={contentRef} className="flex-1 overflow-y-auto" style={{ overscrollBehavior: 'contain' }}>

          {/* ═══ STEP 1: Вид робіт ═══ */}
          {step === 'work_types' && (
            <div className="p-4 flex flex-col gap-1.5">
              {/* AI рекомендації тайпів */}
              {aiSuggestions.length > 0 && aiSuggestions[0].isWorkType && (
                <div className="mb-2">
                  <div className="text-xs font-semibold mb-2" style={{ color: ACCENT }}>AI рекомендує:</div>
                  {aiSuggestions.map((s, i) => {
                    const wt = WORK_TYPES.find(w => w.id === s.workId);
                    if (!wt) return null;
                    return (
                      <button key={i} onClick={() => { setAiSuggestions([]); selectWork(wt.id); }}
                        className="w-full p-3 rounded-xl border text-left mb-2"
                        style={{ borderColor: ACCENT, borderWidth: '1.5px', background: '#fffbeb', cursor: 'pointer' }}
                      >
                        <div className="text-sm font-semibold" style={{ color: BRAND }}>{wt.name}</div>
                        <div className="text-xs mt-0.5" style={{ color: '#92400e' }}>{s.reason}</div>
                      </button>
                    );
                  })}
                  <div className="border-t mt-2 mb-2" style={{ borderColor: '#e5e7eb' }} />
                </div>
              )}
              {WORK_TYPES.filter(wt => wt.available).map(wt => (
                <button key={wt.id} onClick={() => selectWork(wt.id)}
                  className="flex items-center justify-between px-4 py-3.5 bg-white border rounded-xl text-left transition-all"
                  style={{ borderColor: '#e5e7eb', cursor: 'pointer', borderWidth: '1.5px' }}
                >
                  <span className="text-sm font-semibold" style={{ color: BRAND }}>{wt.name}</span>
                </button>
              ))}
            </div>
          )}

          {/* ═══ STEP 2: Технології ═══ */}
          {step === 'technologies' && !showCreateMode && (
            <div>
              {/* Loading */}
              {dataLoading && (
                <div className="flex items-center justify-center gap-2 py-10">
                  <Loader className="w-5 h-5 animate-spin" style={{ color: BRAND }} />
                  <span className="text-sm text-gray-400">Завантаження...</span>
                </div>
              )}

              {!dataLoading && (
                <>
                  {/* Теги */}
                  <div className="px-4 py-2.5 flex flex-wrap gap-1.5 border-b" style={{ borderColor: '#f3f4f6', background: '#fafaf9' }}>
                    {tags.map(tag => {
                      const on = activeTags.includes(tag.id);
                      return (
                        <button key={tag.id} onClick={() => setActiveTags(prev => on ? prev.filter(t => t !== tag.id) : [...prev, tag.id])}
                          className="px-3 py-1.5 rounded-lg text-xs font-medium border transition-all"
                          style={{ borderColor: on ? BRAND : '#d1d5db', borderWidth: '1.5px', background: on ? BRAND : '#fff', color: on ? '#fff' : '#6b7280', cursor: 'pointer' }}
                        >{tag.label}</button>
                      );
                    })}
                  </div>

                  {/* AI пропозиції */}
                  {aiSuggestions.length > 0 && (
                    <div className="px-4 pt-3 pb-1">
                      <div className="text-xs font-semibold mb-2" style={{ color: ACCENT }}>AI рекомендує:</div>
                      {aiSuggestions.map((s, i) => {
                        const tech = dbTechnologies.find(t => t.id === s.techId);
                        if (!tech) return null;
                        return (
                          <button key={i} onClick={() => selectTech(tech)}
                            className="w-full p-3 rounded-xl border text-left transition-all mb-2"
                            style={{ borderColor: ACCENT, borderWidth: '1.5px', background: '#fffbeb' }}
                          >
                            <div className="text-sm font-semibold" style={{ color: BRAND }}>{tech.name}</div>
                            <div className="text-xs mt-1" style={{ color: '#92400e' }}>{s.reason}</div>
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {/* Картки */}
                  <div className="p-4 flex flex-col gap-2">
                    {filteredTechs.length === 0 && (
                      <div className="text-center py-10 text-gray-400 text-sm">Немає технологій для обраних фільтрів</div>
                    )}
                    {filteredTechs.map(tech => {
                      const isExp = expandedCard === tech.id;
                      return (
                        <div key={tech.id} className="border rounded-xl overflow-hidden transition-all" style={{ borderColor: isExp ? BRAND : '#e5e7eb', borderWidth: '1.5px' }}>
                          <div onClick={() => setExpandedCard(isExp ? null : tech.id)} className="px-4 py-3 cursor-pointer">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-sm font-semibold" style={{ color: BRAND }}>{tech.name}</span>
                              {tech.verified
                                ? <span className="text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded-md font-semibold">Перевірена</span>
                                : <span className="text-[10px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded-md font-semibold">На перевірці</span>
                              }
                            </div>
                            <div className="text-[11px] text-gray-400">{tech.layers.filter(l => !l.mortarMode && !l.lintelMode).length + (tech.layers.some(l => l.mortarMode) ? 1 : 0) + (tech.layers.some(l => l.lintelMode) ? 1 : 0)} шарів · {tech.brand}</div>
                          </div>

                          {isExp && (
                            <div style={{ borderTop: '1px solid #f3f4f6' }}>
                              <div className="px-4 py-2 text-xs text-gray-500">{tech.description}</div>
                              <div className="px-4 pb-2">
                                {(() => {
                                  // Фільтруємо: без mode-дублів, показуємо одну назву для груп
                                  const visible = [];
                                  let hasMortar = false, hasLintel = false;
                                  tech.layers.forEach(l => {
                                    if (l.mortarMode) {
                                      if (!hasMortar) { visible.push({ name: 'Кладочний розчин', hasThickness: false }); hasMortar = true; }
                                    } else if (l.lintelMode) {
                                      if (!hasLintel) { visible.push({ name: 'Перемичка', hasThickness: false }); hasLintel = true; }
                                    } else {
                                      visible.push(l);
                                    }
                                  });
                                  return visible.map((l, i) => (
                                    <div key={i} className="flex items-center justify-between py-1.5" style={{ borderBottom: i < visible.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
                                      <span className="text-[13px] text-gray-600">{i + 1}. {l.name}</span>
                                      {l.hasThickness && (
                                        <span className="text-[11px] font-semibold px-2 py-0.5 rounded-md" style={{ color: BRAND, background: '#f0f4f8' }}>{l.defaultThickness} мм</span>
                                      )}
                                    </div>
                                  ));
                                })()}
                              </div>
                              {tech.notes && (
                                <div className="px-4 pb-2 text-xs" style={{ color: '#92400e' }}>{tech.notes}</div>
                              )}
                              <div className="px-4 pb-3 flex gap-2">
                                <button onClick={() => selectTech(tech)} className="flex-1 py-2.5 text-white text-sm font-semibold rounded-xl" style={{ background: BRAND }}>
                                  Розрахувати
                                </button>
                                <button onClick={() => copyTech(tech)} className="py-2.5 px-4 text-sm font-semibold rounded-xl border" style={{ color: BRAND, borderColor: '#e5e7eb', borderWidth: '1.5px', background: '#fff' }}
                                  title="Копіювати та редагувати"
                                >
                                  <Copy style={{ width: 16, height: 16 }} />
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {/* Кнопка створення технології — тимчасово прихована */}
                    {false && !isGuest && (
                      <button onClick={startFromScratch}
                        className="w-full py-3 rounded-xl border-2 border-dashed text-sm font-semibold transition-all"
                        style={{ borderColor: '#d1d5db', color: '#6b7280', background: 'transparent', cursor: 'pointer' }}
                      >
                        + Створити свою технологію
                      </button>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {/* ═══ STEP 2b: Конструктор технології ═══ */}
          {step === 'technologies' && showCreateMode && (
            <div className="p-4">
              {/* Назва */}
              <div className="mb-4">
                <label className="text-[11px] text-gray-400 mb-1 block">Назва технології</label>
                <input
                  type="text"
                  value={customTechName}
                  onChange={e => setCustomTechName(e.target.value)}
                  placeholder="Наприклад: Моя стяжка для ванної"
                  style={{ ...inputStyle, fontSize: '16px' }}
                  onFocus={e => { e.target.style.borderColor = BRAND; }}
                  onBlur={e => { e.target.style.borderColor = '#e5e7eb'; }}
                />
              </div>

              {/* Шари */}
              <div className="mb-3">
                <div className="text-[13px] font-semibold mb-2" style={{ color: BRAND }}>
                  Шари пирога
                  {customLayers.length > 0 && <span className="font-normal text-gray-400 ml-2 text-xs">{customLayers.length} шарів</span>}
                </div>

                {customLayers.length === 0 && (
                  <div className="text-center py-6 text-gray-400 text-xs border rounded-xl mb-3" style={{ borderColor: '#e5e7eb', borderStyle: 'dashed' }}>
                    Додайте шари з бібліотеки
                  </div>
                )}

                {customLayers.map((l, i) => (
                  <div key={l._uid} className="flex items-center gap-2 py-2 px-3 mb-1.5 rounded-lg" style={{ background: '#fafaf9', border: '1px solid #f3f4f6' }}>
                    <span className="text-[11px] text-gray-400 w-4 flex-shrink-0">{i + 1}</span>
                    <span className="flex-1 text-[13px] text-gray-600">{l.name}</span>
                    {l.hasThickness && (
                      <span className="text-[11px] font-semibold px-2 py-0.5 rounded-md" style={{ color: BRAND, background: '#f0f4f8' }}>{l.defaultThickness || 50} мм</span>
                    )}
                    <button onClick={() => removeCustomLayer(l._uid)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}>
                      <Trash2 style={{ width: 14, height: 14, color: '#d1d5db' }} />
                    </button>
                  </div>
                ))}

                {/* Бібліотека шарів */}
                {showLayerLibrary ? (
                  <div className="border rounded-xl overflow-hidden mt-2" style={{ borderColor: '#e5e7eb' }}>
                    <div className="px-3 py-2 text-xs font-semibold" style={{ color: BRAND, background: '#fafaf9' }}>Оберіть шар</div>
                    {LAYER_LIBRARY.map((ll, i) => (
                      <button key={ll.id} onClick={() => addLayerFromLibrary(ll)}
                        className="w-full px-3 py-2.5 text-left text-[13px] text-gray-600 transition-all"
                        style={{ borderTop: '1px solid #f3f4f6', background: '#fff', border: 'none', borderBottom: i < LAYER_LIBRARY.length - 1 ? '1px solid #f3f4f6' : 'none', cursor: 'pointer', fontFamily: 'inherit', display: 'block', width: '100%' }}
                        onMouseEnter={e => { e.target.style.background = '#fafaf9'; }}
                        onMouseLeave={e => { e.target.style.background = '#fff'; }}
                      >
                        {ll.name}
                        {ll.hasThickness && <span className="text-[10px] text-gray-400 ml-2">з товщиною</span>}
                      </button>
                    ))}
                    <button onClick={() => setShowLayerLibrary(false)}
                      className="w-full px-3 py-2 text-xs text-gray-400 text-center"
                      style={{ background: '#fafaf9', border: 'none', borderTop: '1px solid #f3f4f6', cursor: 'pointer' }}
                    >Закрити</button>
                  </div>
                ) : (
                  <button onClick={() => setShowLayerLibrary(true)}
                    className="w-full py-2.5 rounded-xl border text-xs font-medium transition-all mt-1"
                    style={{ borderColor: '#e5e7eb', borderWidth: '1.5px', color: BRAND, background: '#fff', cursor: 'pointer' }}
                  >+ Додати шар</button>
                )}
              </div>

              {/* Кнопки */}
              <div className="flex flex-col gap-2 mt-4">
                <div className="flex gap-2">
                  <button onClick={useCustomTech}
                    disabled={!customTechName.trim() || customLayers.length === 0}
                    className="flex-1 py-2.5 text-white text-[13px] font-semibold rounded-xl disabled:opacity-30 transition-all"
                    style={{ background: BRAND }}
                  >Розрахувати</button>
                  <button onClick={() => setShowCreateMode(false)}
                    className="py-2.5 px-5 text-[13px] font-semibold rounded-xl border transition-all"
                    style={{ color: '#6b7280', borderColor: '#e5e7eb', borderWidth: '1.5px', background: '#fff', cursor: 'pointer' }}
                  >Скасувати</button>
                </div>
                <button
                  onClick={() => {
                    // TODO: зберегти в Supabase таблицю calc_technologies
                    // { name, work_type, layers: JSON, author_id, verified: false }
                    if (window.showToast) window.showToast('Технологію відправлено на перевірку!');
                  }}
                  disabled={!customTechName.trim() || customLayers.length === 0}
                  className="w-full py-2.5 text-[13px] font-semibold rounded-xl border disabled:opacity-30 transition-all"
                  style={{ color: ACCENT, borderColor: ACCENT, borderWidth: '1.5px', background: '#fffbeb', cursor: 'pointer' }}
                >Зберегти і відправити на перевірку</button>
              </div>
            </div>
          )}

          {/* ═══ STEP 3: Розрахунок ═══ */}
          {step === 'calculate' && selectedTech && (
            <div className="p-4">
              {/* Розміри */}
              <div className="mb-4">
                <div className="text-[13px] font-semibold mb-2" style={{ color: BRAND }}>
                  {isConstruct ? 'Параметри конструкції' : isCeiling ? 'Параметри стелі' : isCeilingPrep ? 'Параметри стелі' : isPartition ? 'Параметри перегородки' : isTiling ? 'Параметри укладки' : (isPlaster || isCladding || isMasonry) ? 'Параметри стін' : 'Розміри приміщення'}
                </div>

                {isTiling ? (
                  <>
                    {/* Тип плитки */}
                    <div className="mb-3">
                      <div className="text-[11px] text-gray-400 mb-1">Тип плитки</div>
                      <div className="flex gap-2">
                        {[{ v: 'standard', l: 'Керамограніт' }, { v: 'mosaic', l: 'Мозаїка' }].map(({ v, l }) => (
                          <label key={v} className="flex items-center gap-1.5 text-[12px] cursor-pointer px-3 py-1.5 rounded-lg border"
                            style={{ borderColor: (dims.tilingType || 'standard') === v ? BRAND : '#e5e7eb', background: (dims.tilingType || 'standard') === v ? BRAND + '10' : '#fff', color: (dims.tilingType || 'standard') === v ? BRAND : '#6b7280', fontWeight: (dims.tilingType || 'standard') === v ? 600 : 400 }}>
                            <input type="radio" name="tilingType" value={v} checked={(dims.tilingType || 'standard') === v}
                              onChange={() => setDims(d => ({ ...d, tilingType: v }))} style={{ display: 'none' }} />
                            {l}
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Площа */}
                    <div className="flex gap-2.5 mb-3">
                      <div className="flex-1">
                        <label className="text-[11px] text-gray-400 mb-1 block">Площа укладки, м²</label>
                        <input type="number" inputMode="decimal" value={dims.tilingArea || ''}
                          onChange={e => setDims(d => ({ ...d, tilingArea: e.target.value }))}
                          placeholder="0" style={{ ...inputStyle, fontSize: '16px' }}
                          onFocus={e => { e.target.style.borderColor = BRAND; }}
                          onBlur={e => { e.target.style.borderColor = '#e5e7eb'; }}
                        />
                      </div>
                    </div>

                    {/* Розміри плитки і шов — тільки для керамограніту */}
                    {(dims.tilingType || 'standard') === 'standard' && (
                      <>
                        <div className="flex gap-2.5 mb-3">
                          <div className="flex-1">
                            <label className="text-[11px] text-gray-400 mb-1 block">Ширина плитки, мм</label>
                            <input type="number" inputMode="numeric" value={dims.tileW || ''}
                              onChange={e => setDims(d => ({ ...d, tileW: e.target.value }))}
                              placeholder="600" style={{ ...inputStyle, fontSize: '16px', color: dims.tileW ? undefined : '#d1d5db' }}
                              onFocus={e => { e.target.style.borderColor = BRAND; e.target.style.color = '#111827'; }}
                              onBlur={e => { e.target.style.borderColor = '#e5e7eb'; e.target.style.color = dims.tileW ? '#111827' : '#d1d5db'; }}
                            />
                          </div>
                          <div className="flex-1">
                            <label className="text-[11px] text-gray-400 mb-1 block">Висота плитки, мм</label>
                            <input type="number" inputMode="numeric" value={dims.tileH || ''}
                              onChange={e => setDims(d => ({ ...d, tileH: e.target.value }))}
                              placeholder="600" style={{ ...inputStyle, fontSize: '16px', color: dims.tileH ? undefined : '#d1d5db' }}
                              onFocus={e => { e.target.style.borderColor = BRAND; e.target.style.color = '#111827'; }}
                              onBlur={e => { e.target.style.borderColor = '#e5e7eb'; e.target.style.color = dims.tileH ? '#111827' : '#d1d5db'; }}
                            />
                          </div>
                          <div style={{ width: 80 }}>
                            <label className="text-[11px] text-gray-400 mb-1 block">Шов, мм</label>
                            <input type="number" inputMode="decimal" value={dims.groutWidth || ''}
                              onChange={e => setDims(d => ({ ...d, groutWidth: e.target.value }))}
                              placeholder="2" style={{ ...inputStyle, fontSize: '16px', color: dims.groutWidth ? undefined : '#d1d5db' }}
                              onFocus={e => { e.target.style.borderColor = BRAND; e.target.style.color = '#111827'; }}
                              onBlur={e => { e.target.style.borderColor = '#e5e7eb'; e.target.style.color = dims.groutWidth ? '#111827' : '#d1d5db'; }}
                            />
                          </div>
                        </div>

                        {/* Хрестики або СВП */}
                        <div className="mb-3">
                          <div className="text-[11px] text-gray-400 mb-1">Фіксація шва</div>
                          <div className="flex gap-2">
                            {[{ v: 'crosses', l: 'Хрестики' }, { v: 'svp', l: 'СВП' }].map(({ v, l }) => (
                              <label key={v} className="flex items-center gap-1.5 text-[12px] cursor-pointer px-3 py-1.5 rounded-lg border"
                                style={{ borderColor: (dims.svpMode || 'crosses') === v ? BRAND : '#e5e7eb', background: (dims.svpMode || 'crosses') === v ? BRAND + '10' : '#fff', color: (dims.svpMode || 'crosses') === v ? BRAND : '#6b7280', fontWeight: (dims.svpMode || 'crosses') === v ? 600 : 400 }}>
                                <input type="radio" name="svpMode" value={v} checked={(dims.svpMode || 'crosses') === v}
                                  onChange={() => setDims(d => ({ ...d, svpMode: v }))} style={{ display: 'none' }} />
                                {l}
                              </label>
                            ))}
                          </div>
                        </div>
                      </>
                    )}

                    {/* Тепла підлога */}
                    <label className="flex items-center gap-2 text-[12px] text-gray-600 cursor-pointer mb-3">
                      <input type="checkbox" checked={!!dims.tilingWarmFloor}
                        onChange={e => setDims(d => ({ ...d, tilingWarmFloor: e.target.checked }))}
                        style={{ accentColor: BRAND, width: 16, height: 16 }}
                      />
                      Тепла підлога (еластичний клей)
                    </label>

                    {/* Гідроізоляція */}
                    <div className="text-[11px] font-medium text-gray-500 mb-1.5 mt-1">Гідроізоляція</div>
                    <div className="flex gap-2.5 mb-2">
                      <div className="flex-1">
                        <label className="text-[11px] text-gray-400 mb-1 block">Площа гідроізоляції, м²</label>
                        <input type="number" inputMode="decimal" value={dims.hydroArea || ''}
                          onChange={e => setDims(d => ({ ...d, hydroArea: e.target.value }))}
                          placeholder="0" style={{ ...inputStyle, fontSize: '16px', color: dims.hydroArea ? undefined : '#d1d5db' }}
                          onFocus={e => { e.target.style.borderColor = BRAND; e.target.style.color = '#111827'; }}
                          onBlur={e => { e.target.style.borderColor = '#e5e7eb'; e.target.style.color = dims.hydroArea ? '#111827' : '#d1d5db'; }}
                        />
                      </div>
                      <div className="flex-1">
                        <label className="text-[11px] text-gray-400 mb-1 block">Зовн. кути, п.м.</label>
                        <input type="number" inputMode="decimal" value={dims.outerCorners || ''}
                          onChange={e => setDims(d => ({ ...d, outerCorners: e.target.value }))}
                          placeholder="0" style={{ ...inputStyle, fontSize: '16px', color: dims.outerCorners ? undefined : '#d1d5db' }}
                          onFocus={e => { e.target.style.borderColor = BRAND; e.target.style.color = '#111827'; }}
                          onBlur={e => { e.target.style.borderColor = '#e5e7eb'; e.target.style.color = dims.outerCorners ? '#111827' : '#d1d5db'; }}
                        />
                      </div>
                      <div className="flex-1">
                        <label className="text-[11px] text-gray-400 mb-1 block">Внутр. кути, п.м.</label>
                        <input type="number" inputMode="decimal" value={dims.innerCorners || ''}
                          onChange={e => setDims(d => ({ ...d, innerCorners: e.target.value }))}
                          placeholder="0" style={{ ...inputStyle, fontSize: '16px', color: dims.innerCorners ? undefined : '#d1d5db' }}
                          onFocus={e => { e.target.style.borderColor = BRAND; e.target.style.color = '#111827'; }}
                          onBlur={e => { e.target.style.borderColor = '#e5e7eb'; e.target.style.color = dims.innerCorners ? '#111827' : '#d1d5db'; }}
                        />
                      </div>
                    </div>

                    {/* Отвори */}
                    <div className="text-[11px] font-medium text-gray-500 mb-1.5 mt-2">Отвори під сантехніку</div>
                    {(dims.holes || []).map((hole, idx) => (
                      <div key={idx} className="flex gap-2 mb-2 items-end">
                        <div className="flex-1">
                          <label className="text-[11px] text-gray-400 mb-1 block">Призначення</label>
                          <CustomSelect
                            value={hole.id || HOLE_PRESETS.find(p => p.label === hole.label)?.id || 'socket'}
                            options={HOLE_PRESETS.map(p => ({ id: p.id, name: p.label }))}
                            onChange={id => {
                              const preset = HOLE_PRESETS.find(p => p.id === id);
                              const newHoles = [...(dims.holes || [])];
                              newHoles[idx] = { id, label: preset ? preset.label : id, diameter: preset ? preset.diameter : hole.diameter };
                              setDims(d => ({ ...d, holes: newHoles }));
                            }}
                          />
                        </div>
                        <div style={{ width: 72 }}>
                          <label className="text-[11px] text-gray-400 mb-1 block">Ø, мм</label>
                          <input type="number" inputMode="numeric" value={hole.diameter}
                            onChange={e => {
                              const newHoles = [...(dims.holes || [])];
                              newHoles[idx] = { ...newHoles[idx], diameter: e.target.value };
                              setDims(d => ({ ...d, holes: newHoles }));
                            }}
                            style={{ ...inputStyle, fontSize: '14px' }}
                            onFocus={e => { e.target.style.borderColor = BRAND; }}
                            onBlur={e => { e.target.style.borderColor = '#e5e7eb'; }}
                          />
                        </div>
                        <button onClick={() => { const h = [...(dims.holes||[])]; h.splice(idx,1); setDims(d=>({...d,holes:h})); }}
                          style={{ height: 40, width: 36, borderRadius: 8, border: '1.5px solid #fecaca', background: '#fff5f5', color: '#f87171', cursor: 'pointer', fontSize: 16, flexShrink: 0 }}>✕</button>
                      </div>
                    ))}
                    <button onClick={() => setDims(d => ({ ...d, holes: [...(d.holes||[]), { id: HOLE_PRESETS[0].id, label: HOLE_PRESETS[0].label, diameter: HOLE_PRESETS[0].diameter }] }))}
                      className="text-[12px] font-medium px-3 py-1.5 rounded-lg border"
                      style={{ borderColor: BRAND, color: BRAND, background: BRAND + '08', cursor: 'pointer' }}>
                      + Додати отвір
                    </button>
                  </>
                ) : isConstruct ? (
                  <>
                    {/* Конструкції з ГКЛ — UI залежить від технології */}
                    {(() => {
                      const tags = selectedTech?.tags || [];
                      const gcInput = (key, label) => (
                        <div key={key} className="flex-1">
                          <label className="text-[11px] text-gray-400 mb-1 block">{label}</label>
                          <input type="number" inputMode="decimal" value={dims[key]}
                            onChange={e => setDims(d => ({ ...d, [key]: e.target.value }))}
                            placeholder="" style={{ ...inputStyle, fontSize: '16px' }}
                            onFocus={e => { e.target.style.borderColor = BRAND; }}
                            onBlur={e => { e.target.style.borderColor = '#e5e7eb'; }}
                          />
                        </div>
                      );
                      const stepSelector = (
                        <div className="mb-2">
                          <div className="text-[11px] text-gray-400 mb-1">Крок профілів</div>
                          <div className="flex gap-2">
                            {[{ v: '0.6', l: '600 мм' }, { v: '0.4', l: '400 мм' }].map(({ v, l }) => (
                              <label key={v} className="flex items-center gap-1.5 text-[12px] cursor-pointer px-3 py-1.5 rounded-lg border"
                                style={{ borderColor: (dims.profileStep || '0.6') === v ? BRAND : '#e5e7eb', background: (dims.profileStep || '0.6') === v ? BRAND + '10' : '#fff', color: (dims.profileStep || '0.6') === v ? BRAND : '#6b7280', fontWeight: (dims.profileStep || '0.6') === v ? 600 : 400 }}>
                                <input type="radio" name="gcStep" value={v} checked={(dims.profileStep || '0.6') === v}
                                  onChange={() => setDims(d => ({ ...d, profileStep: v }))} style={{ display: 'none' }} />
                                {l}
                              </label>
                            ))}
                          </div>
                        </div>
                      );

                      if (tags.includes('gc_column')) return (<>
                        <div className="flex gap-2.5 mb-2">{gcInput('columnA','Сторона А, м')}{gcInput('columnB','Сторона Б, м')}{gcInput('height','Висота, м')}</div>
                        {stepSelector}
                      </>);

                      if (tags.includes('gc_box_p') || tags.includes('gc_box_g')) return (<>
                        <div className="flex gap-2.5 mb-2">{gcInput('columnA','Сторона А, м')}{gcInput('columnB','Сторона Б, м')}{gcInput('boxLength','Довжина, м')}</div>
                        {stepSelector}
                      </>);

                      if (tags.includes('gc_install')) return (<>
                        <div className="flex gap-2.5 mb-2">{gcInput('gcWidth','Ширина, м')}{gcInput('gcDepth','Глибина, м')}{gcInput('height','Висота, м')}</div>
                        {stepSelector}
                      </>);

                      if (tags.includes('gc_box_h')) return (<>
                        <div className="flex gap-2.5 mb-2">{gcInput('gcWidth','Ширина, м')}{gcInput('height','Висота, м')}{gcInput('gcDepth','Глибина, м')}</div>
                        {stepSelector}
                      </>);

                      if (tags.includes('gc_niche')) return (<>
                        <div className="flex gap-2.5 mb-2">{gcInput('gcWidth','Ширина, м')}{gcInput('height','Висота, м')}{gcInput('gcDepth','Глибина, м')}</div>
                        <div className="mb-2">
                          <div className="text-[11px] text-gray-400 mb-1">Кількість сторін</div>
                          <div className="flex gap-2">
                            {['3', '4'].map(s => (
                              <label key={s} className="flex items-center gap-1.5 text-[12px] cursor-pointer px-3 py-1.5 rounded-lg border"
                                style={{ borderColor: (dims.nicheSides || '3') === s ? BRAND : '#e5e7eb', background: (dims.nicheSides || '3') === s ? BRAND + '10' : '#fff', color: (dims.nicheSides || '3') === s ? BRAND : '#6b7280', fontWeight: (dims.nicheSides || '3') === s ? 600 : 400 }}>
                                <input type="radio" name="nicheSides" value={s} checked={(dims.nicheSides || '3') === s}
                                  onChange={() => setDims(d => ({ ...d, nicheSides: s }))} style={{ display: 'none' }} />
                                {s} {s === '3' ? '(верх + бічні)' : '(верх + низ + бічні)'}
                              </label>
                            ))}
                          </div>
                        </div>
                        {stepSelector}
                      </>);

                      if (tags.includes('gc_shelf')) return (<>
                        <div className="flex gap-2.5 mb-2">{gcInput('shelfLength','Довжина, м')}{gcInput('shelfDepth','Глибина, м')}{gcInput('shelfCount','Кількість')}</div>
                        {stepSelector}
                      </>);

                      return null;
                    })()}

                    {/* Підсумок площі */}
                    {result && result.area > 0 && (
                      <div className="mt-2 text-xs text-gray-500">
                        <span>Площа обшивки: <b style={{ color: BRAND }}>{result.area.toFixed(2)} м²</b></span>
                      </div>
                    )}
                  </>
                ) : isPartition ? (
                  <>
                    {/* Перегородка: висота × довжина */}
                    <div className="flex gap-2.5 mb-2">
                      {[{ key: 'height', label: 'Висота, м', ph: '' }, { key: 'partitionLength', label: 'Довжина перегородки, м', ph: '' }].map(({ key, label, ph }) => (
                        <div key={key} className="flex-1">
                          <label className="text-[11px] text-gray-400 mb-1 block">{label}</label>
                          <input type="number" inputMode="decimal" value={dims[key]}
                            onChange={e => setDims(d => ({ ...d, [key]: e.target.value }))}
                            placeholder={ph} style={{ ...inputStyle, fontSize: '16px' }}
                            onFocus={e => { e.target.style.borderColor = BRAND; }}
                            onBlur={e => { e.target.style.borderColor = '#e5e7eb'; }}
                          />
                        </div>
                      ))}
                    </div>

                    {/* Крок профілю (partition) */}
                    {!(selectedTech?.tags || []).includes('pt_wood_frame') && (
                      <div className="mb-2">
                        <div className="text-[11px] text-gray-400 mb-1">Крок вертикальних профілів</div>
                        <div className="flex gap-2">
                          {[{ v: '0.6', l: '600 мм (стандарт)' }, { v: '0.4', l: '400 мм (посилений)' }].map(({ v, l }) => (
                            <label key={v} className="flex items-center gap-1.5 text-[12px] cursor-pointer px-3 py-1.5 rounded-lg border"
                              style={{ borderColor: (dims.profileStep || '0.6') === v ? BRAND : '#e5e7eb', background: (dims.profileStep || '0.6') === v ? BRAND + '10' : '#fff', color: (dims.profileStep || '0.6') === v ? BRAND : '#6b7280', fontWeight: (dims.profileStep || '0.6') === v ? 600 : 400 }}>
                              <input type="radio" name="profileStepPt" value={v} checked={(dims.profileStep || '0.6') === v}
                                onChange={() => setDims(d => ({ ...d, profileStep: v }))}
                                style={{ display: 'none' }}
                              />
                              {l}
                            </label>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Дверні отвори — динамічний список */}
                    <div className="mb-2 mt-3">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-[11px] font-medium text-gray-500">Дверні отвори</span>
                        <button onClick={() => setDims(d => ({ ...d, doorOpenings: [...(d.doorOpenings||[]), { w:'', h:'' }] }))}
                          className="text-[11px] px-2 py-0.5 rounded-lg border"
                          style={{ borderColor: BRAND, color: BRAND, background: 'transparent', cursor: 'pointer' }}>
                          + додати
                        </button>
                      </div>
                      {(dims.doorOpenings||[]).length === 0 && <div className="text-[11px] text-gray-400 py-1">Немає отворів</div>}
                      {(dims.doorOpenings||[]).map((item, i) => (
                        <div key={i} className="flex gap-2 mb-1.5 items-end">
                          <div className="text-[11px] text-gray-400 pb-2.5" style={{ width: 20 }}>#{i+1}</div>
                          <div className="flex-1">
                            {i === 0 && <label className="text-[11px] text-gray-400 mb-1 block">Ширина, м</label>}
                            <input type="number" inputMode="decimal" value={item.w} placeholder="0.00"
                              onChange={e => setDims(d => { const arr=[...(d.doorOpenings||[])]; arr[i]={...arr[i],w:e.target.value}; return {...d,doorOpenings:arr}; })}
                              style={{ ...inputStyle, fontSize: '14px' }}
                              onFocus={e => { e.target.style.borderColor = BRAND; }}
                              onBlur={e => { e.target.style.borderColor = '#e5e7eb'; }}
                            />
                          </div>
                          <div className="flex-1">
                            {i === 0 && <label className="text-[11px] text-gray-400 mb-1 block">Висота, м</label>}
                            <input type="number" inputMode="decimal" value={item.h} placeholder="0.00"
                              onChange={e => setDims(d => { const arr=[...(d.doorOpenings||[])]; arr[i]={...arr[i],h:e.target.value}; return {...d,doorOpenings:arr}; })}
                              style={{ ...inputStyle, fontSize: '14px' }}
                              onFocus={e => { e.target.style.borderColor = BRAND; }}
                              onBlur={e => { e.target.style.borderColor = '#e5e7eb'; }}
                            />
                          </div>
                          <button onClick={() => setDims(d => ({ ...d, doorOpenings: (d.doorOpenings||[]).filter((_,idx)=>idx!==i) }))}
                            style={{ background:'transparent', border:'none', cursor:'pointer', color:'#9ca3af', paddingBottom:6, fontSize:16 }}>✕</button>
                        </div>
                      ))}
                    </div>

                    {/* Підсумок */}
                    {dims.height && dims.partitionLength && (() => {
                      const h = parseFloat(dims.height) || 0;
                      const pl = parseFloat(dims.partitionLength) || 0;
                      const gross = h * pl;
                      const da = (dims.doorOpenings||[]).reduce((s,o)=>s+(parseFloat(o.w)||0)*(parseFloat(o.h)||0),0);
                      const net = Math.max(gross - da, 0);
                      return (
                        <div className="mt-2 text-xs text-gray-500 flex flex-wrap gap-x-4 gap-y-1">
                          <span>Загальна: <b>{gross.toFixed(1)} м²</b></span>
                          {da > 0 && <span>Двері: <b>−{da.toFixed(1)} м²</b></span>}
                          <span>Чиста площа: <b style={{ color: BRAND }}>{net.toFixed(1)} м²</b></span>
                        </div>
                      );
                    })()}
                  </>
                ) : isCeiling ? (
                  <>
                    {/* Стеля: довжина × ширина */}
                    <div className="flex gap-2.5 mb-2">
                      {[{ key: 'length', label: 'Довжина кімнати, м' }, { key: 'width', label: 'Ширина кімнати, м' }].map(({ key, label }) => (
                        <div key={key} className="flex-1">
                          <label className="text-[11px] text-gray-400 mb-1 block">{label}</label>
                          <input type="number" inputMode="decimal" value={dims[key]}
                            onChange={e => setDims(d => ({ ...d, [key]: e.target.value }))}
                            placeholder="" style={{ ...inputStyle, fontSize: '16px' }}
                            onFocus={e => { e.target.style.borderColor = BRAND; }}
                            onBlur={e => { e.target.style.borderColor = '#e5e7eb'; }}
                          />
                        </div>
                      ))}
                    </div>
                    {/* Висота опускання */}
                    <div className="flex gap-2.5 mb-2">
                      <div className="flex-1">
                        <label className="text-[11px] text-gray-400 mb-1 block">Висота опускання, мм</label>
                        <input type="number" inputMode="numeric" value={dims.ceilDrop}
                          onChange={e => setDims(d => ({ ...d, ceilDrop: e.target.value }))}
                          placeholder="100" style={{ ...inputStyle, fontSize: '14px' }}
                          onFocus={e => { e.target.style.borderColor = BRAND; }}
                          onBlur={e => { e.target.style.borderColor = '#e5e7eb'; }}
                        />
                      </div>
                      <div className="flex-1">
                        <div className="text-[11px] text-gray-400 mb-1">Крок профілів</div>
                        <div className="flex gap-2 mt-1">
                          {[{ v: '0.4', l: '400 мм' }, { v: '0.6', l: '600 мм' }].map(({ v, l }) => (
                            <label key={v} className="flex items-center gap-1 text-[12px] cursor-pointer px-2.5 py-1.5 rounded-lg border"
                              style={{ borderColor: (dims.profileStep || '0.4') === v ? BRAND : '#e5e7eb', background: (dims.profileStep || '0.4') === v ? BRAND + '10' : '#fff', color: (dims.profileStep || '0.4') === v ? BRAND : '#6b7280', fontWeight: (dims.profileStep || '0.4') === v ? 600 : 400 }}>
                              <input type="radio" name="ceilStep" value={v} checked={(dims.profileStep || '0.4') === v}
                                onChange={() => setDims(d => ({ ...d, profileStep: v }))} style={{ display: 'none' }} />
                              {l}
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                    {/* Підсумок */}
                    {dims.length && dims.width && (
                      <div className="mt-1.5 text-xs text-gray-500">
                        <span>Площа стелі: <b style={{ color: BRAND }}>{(parseFloat(dims.length) * parseFloat(dims.width)).toFixed(1)} м²</b></span>
                        {dims.ceilDrop && parseFloat(dims.ceilDrop) > 130 && (
                          <span className="ml-3 text-amber-600">Кронштейни з CD (опускання &gt; 130мм)</span>
                        )}
                      </div>
                    )}
                  </>
                ) : isCeilingPrep ? (
                  <>
                    {/* Малярні роботи стеля: площа + периметр + ніші */}
                    <div className="flex gap-2.5 mb-2">
                      <div className="flex-1">
                        <label className="text-[11px] text-gray-400 mb-1 block">Площа стелі, м²</label>
                        <input type="number" inputMode="decimal" value={dims.ceilArea || ''}
                          onChange={e => setDims(d => ({ ...d, ceilArea: e.target.value }))}
                          placeholder="0.00" style={{ ...inputStyle, fontSize: '16px' }}
                          onFocus={e => { e.target.style.borderColor = BRAND; }}
                          onBlur={e => { e.target.style.borderColor = '#e5e7eb'; }}
                        />
                      </div>
                      <div className="flex-1">
                        <label className="text-[11px] text-gray-400 mb-1 block">Периметр стелі, м</label>
                        <input type="number" inputMode="decimal" value={dims.ceilPerimeter || ''}
                          onChange={e => setDims(d => ({ ...d, ceilPerimeter: e.target.value }))}
                          placeholder="0.00" style={{ ...inputStyle, fontSize: '16px' }}
                          onFocus={e => { e.target.style.borderColor = BRAND; }}
                          onBlur={e => { e.target.style.borderColor = '#e5e7eb'; }}
                        />
                      </div>
                    </div>

                    {/* Ніші */}
                    <div className="text-[11px] font-medium text-gray-500 mb-1.5 mt-2">Ніші</div>
                    {(dims.niches || []).map((niche, idx) => (
                      <div key={idx} className="flex gap-2 mb-2 items-end">
                        <div className="flex-1">
                          <label className="text-[11px] text-gray-400 mb-1 block">Довжина, м</label>
                          <input type="number" inputMode="decimal" value={niche.length || ''}
                            onChange={e => {
                              const n = [...(dims.niches || [])];
                              n[idx] = { ...n[idx], length: e.target.value };
                              setDims(d => ({ ...d, niches: n }));
                            }}
                            placeholder="0.00" style={{ ...inputStyle, fontSize: '14px' }}
                            onFocus={e => { e.target.style.borderColor = BRAND; }}
                            onBlur={e => { e.target.style.borderColor = '#e5e7eb'; }}
                          />
                        </div>
                        <div className="flex-1">
                          <label className="text-[11px] text-gray-400 mb-1 block">Глибина торця, м</label>
                          <input type="number" inputMode="decimal" value={niche.depth || ''}
                            onChange={e => {
                              const n = [...(dims.niches || [])];
                              n[idx] = { ...n[idx], depth: e.target.value };
                              setDims(d => ({ ...d, niches: n }));
                            }}
                            placeholder="0.00" style={{ ...inputStyle, fontSize: '14px' }}
                            onFocus={e => { e.target.style.borderColor = BRAND; }}
                            onBlur={e => { e.target.style.borderColor = '#e5e7eb'; }}
                          />
                        </div>
                        <button onClick={() => { const n = [...(dims.niches||[])]; n.splice(idx,1); setDims(d=>({...d,niches:n})); }}
                          style={{ background:'transparent', border:'none', cursor:'pointer', color:'#9ca3af', paddingBottom:6, fontSize:16, flexShrink: 0 }}>✕</button>
                      </div>
                    ))}
                    <button onClick={() => setDims(d => ({ ...d, niches: [...(d.niches||[]), { length: '', depth: '' }] }))}
                      className="text-[12px] font-medium px-3 py-1.5 rounded-lg border"
                      style={{ borderColor: BRAND, color: BRAND, background: BRAND + '08', cursor: 'pointer', marginBottom: 8 }}>
                      + Додати нішу
                    </button>

                    {/* Підсумок */}
                    {dims.ceilArea && (
                      <div className="mt-2 text-xs text-gray-500 flex flex-wrap gap-x-4 gap-y-1">
                        <span>Площа стелі: <b style={{ color: BRAND }}>{parseFloat(dims.ceilArea).toFixed(1)} м²</b></span>
                        {(dims.niches||[]).some(n => n.length && n.depth) && (
                          <span>+ ніші: <b style={{ color: BRAND }}>{(dims.niches||[]).reduce((s,n)=>s+(parseFloat(n.length)||0)*(parseFloat(n.depth)||0),0).toFixed(2)} м²</b></span>
                        )}
                      </div>
                    )}

                    {/* Штроби */}
                    <div className="mt-3">
                      <label className="flex items-center gap-2 text-[12px] text-gray-600 cursor-pointer mb-2">
                        <input type="checkbox" checked={!!dims.hasGrooves}
                          onChange={e => setDims(d => ({ ...d, hasGrooves: e.target.checked }))}
                          style={{ accentColor: BRAND, width: 16, height: 16 }}
                        />
                        Заробка штроб
                      </label>
                      {dims.hasGrooves && (
                        <div className="ml-6 mb-2">
                          <label className="text-[11px] text-gray-400 mb-1 block">Довжина штроб, м.п.</label>
                          <input type="number" inputMode="decimal" value={dims.grooveLength}
                            onChange={e => setDims(d => ({ ...d, grooveLength: e.target.value }))}
                            placeholder="0" style={{ ...inputStyle, fontSize: '14px', width: 120 }}
                            onFocus={e => { e.target.style.borderColor = BRAND; }}
                            onBlur={e => { e.target.style.borderColor = '#e5e7eb'; }}
                          />
                        </div>
                      )}
                      <label className="flex items-center gap-2 text-[12px] text-gray-600 cursor-pointer mb-2">
                        <input type="checkbox" checked={!!dims.ceilingJoint}
                          onChange={e => setDims(d => ({ ...d, ceilingJoint: e.target.checked }))}
                          style={{ accentColor: BRAND, width: 16, height: 16 }}
                        />
                        Армування стику стіна-стеля
                      </label>
                      <div className="mt-2">
                        <div className="text-[11px] text-gray-400 mb-1">Тип шліфування</div>
                        <div className="flex gap-2">
                          {[{ val: 'manual', label: 'Ручне (наждачка)' }, { val: 'giraffe', label: 'Жираф (круги)' }].map(({ val, label }) => (
                            <label key={val} className="flex items-center gap-1.5 text-[12px] cursor-pointer"
                              style={{ color: dims.sandingType === val ? BRAND : '#6b7280', fontWeight: dims.sandingType === val ? 600 : 400 }}>
                              <input type="radio" name="sandingType" value={val}
                                checked={dims.sandingType === val}
                                onChange={() => setDims(d => ({ ...d, sandingType: val }))}
                                style={{ accentColor: BRAND, width: 14, height: 14 }}
                              />
                              {label}
                            </label>
                          ))}
                        </div>
                      </div>
                    </div>
                  </>
                ) : !isPlaster && !isCladding && !isMasonry ? (
                  <>
                    {/* Стяжка: довжина × ширина */}
                    <div className="flex gap-2.5">
                      {[{ key: 'length', label: 'Довжина, м' }, { key: 'width', label: 'Ширина, м' }].map(({ key, label }) => (
                        <div key={key} className="flex-1">
                          <label className="text-[11px] text-gray-400 mb-1 block">{label}</label>
                          <input type="number" inputMode="decimal" value={dims[key]}
                            onChange={e => setDims(d => ({ ...d, [key]: e.target.value }))}
                            placeholder="0.00" style={{ ...inputStyle, fontSize: '16px' }}
                            onFocus={e => { e.target.style.borderColor = BRAND; }}
                            onBlur={e => { e.target.style.borderColor = '#e5e7eb'; }}
                          />
                        </div>
                      ))}
                    </div>
                    {dims.length && dims.width && (
                      <div className="mt-1.5 text-xs text-gray-500 flex gap-4">
                        <span>Площа: <b style={{ color: BRAND }}>{(parseFloat(dims.length) * parseFloat(dims.width)).toFixed(1)} м²</b></span>
                        <span>Периметр: <b style={{ color: BRAND }}>{(2 * (parseFloat(dims.length) + parseFloat(dims.width))).toFixed(1)} м</b></span>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    {/* Штукатурка: висота, довжина стін */}
                    <div className="flex gap-2.5 mb-2">
                      {[{ key: 'height', label: 'Висота стін, м' }, { key: 'wallLength', label: 'Довжина стін (периметр), м' }].map(({ key, label }) => (
                        <div key={key} className="flex-1">
                          <label className="text-[11px] text-gray-400 mb-1 block">{label}</label>
                          <input type="number" inputMode="decimal" value={dims[key]}
                            onChange={e => setDims(d => ({ ...d, [key]: e.target.value }))}
                            placeholder="0.00" style={{ ...inputStyle, fontSize: '16px' }}
                            onFocus={e => { e.target.style.borderColor = BRAND; }}
                            onBlur={e => { e.target.style.borderColor = '#e5e7eb'; }}
                          />
                        </div>
                      ))}
                    </div>

                    {/* Дверні отвори — динамічний список */}
                    <div className="mb-2 mt-3">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-[11px] font-medium text-gray-500">Дверні отвори</span>
                        <button onClick={() => setDims(d => ({ ...d, doorOpenings: [...(d.doorOpenings||[]), { w:'', h:'' }] }))}
                          className="text-[11px] px-2 py-0.5 rounded-lg border"
                          style={{ borderColor: BRAND, color: BRAND, background: 'transparent', cursor: 'pointer' }}>
                          + додати
                        </button>
                      </div>
                      {(dims.doorOpenings||[]).length === 0 && <div className="text-[11px] text-gray-400 py-1">Немає отворів</div>}
                      {(dims.doorOpenings||[]).map((item, i) => (
                        <div key={i} className="flex gap-2 mb-1.5 items-end">
                          <div className="text-[11px] text-gray-400 pb-2.5" style={{ width: 20 }}>#{i+1}</div>
                          <div className="flex-1">
                            {i === 0 && <label className="text-[11px] text-gray-400 mb-1 block">Ширина, м</label>}
                            <input type="number" inputMode="decimal" value={item.w} placeholder="0.00"
                              onChange={e => setDims(d => { const arr=[...(d.doorOpenings||[])]; arr[i]={...arr[i],w:e.target.value}; return {...d,doorOpenings:arr}; })}
                              style={{ ...inputStyle, fontSize: '14px' }}
                              onFocus={e => { e.target.style.borderColor = BRAND; }}
                              onBlur={e => { e.target.style.borderColor = '#e5e7eb'; }}
                            />
                          </div>
                          <div className="flex-1">
                            {i === 0 && <label className="text-[11px] text-gray-400 mb-1 block">Висота, м</label>}
                            <input type="number" inputMode="decimal" value={item.h} placeholder="0.00"
                              onChange={e => setDims(d => { const arr=[...(d.doorOpenings||[])]; arr[i]={...arr[i],h:e.target.value}; return {...d,doorOpenings:arr}; })}
                              style={{ ...inputStyle, fontSize: '14px' }}
                              onFocus={e => { e.target.style.borderColor = BRAND; }}
                              onBlur={e => { e.target.style.borderColor = '#e5e7eb'; }}
                            />
                          </div>
                          <button onClick={() => setDims(d => ({ ...d, doorOpenings: (d.doorOpenings||[]).filter((_,idx)=>idx!==i) }))}
                            style={{ background:'transparent', border:'none', cursor:'pointer', color:'#9ca3af', paddingBottom:6, fontSize:16 }}>✕</button>
                        </div>
                      ))}
                    </div>
                    {/* Віконні отвори — динамічний список */}
                    <div className="mb-2 mt-3">
                      <div className="flex items-center justify-between mb-1.5">
                        <span className="text-[11px] font-medium text-gray-500">Віконні отвори</span>
                        <button onClick={() => setDims(d => ({ ...d, windowOpenings: [...(d.windowOpenings||[]), { w:'', h:'' }] }))}
                          className="text-[11px] px-2 py-0.5 rounded-lg border"
                          style={{ borderColor: BRAND, color: BRAND, background: 'transparent', cursor: 'pointer' }}>
                          + додати
                        </button>
                      </div>
                      {(dims.windowOpenings||[]).length === 0 && <div className="text-[11px] text-gray-400 py-1">Немає отворів</div>}
                      {(dims.windowOpenings||[]).map((item, i) => (
                        <div key={i} className="flex gap-2 mb-1.5 items-end">
                          <div className="text-[11px] text-gray-400 pb-2.5" style={{ width: 20 }}>#{i+1}</div>
                          <div className="flex-1">
                            {i === 0 && <label className="text-[11px] text-gray-400 mb-1 block">Ширина, м</label>}
                            <input type="number" inputMode="decimal" value={item.w} placeholder="0.00"
                              onChange={e => setDims(d => { const arr=[...(d.windowOpenings||[])]; arr[i]={...arr[i],w:e.target.value}; return {...d,windowOpenings:arr}; })}
                              style={{ ...inputStyle, fontSize: '14px' }}
                              onFocus={e => { e.target.style.borderColor = BRAND; }}
                              onBlur={e => { e.target.style.borderColor = '#e5e7eb'; }}
                            />
                          </div>
                          <div className="flex-1">
                            {i === 0 && <label className="text-[11px] text-gray-400 mb-1 block">Висота, м</label>}
                            <input type="number" inputMode="decimal" value={item.h} placeholder="0.00"
                              onChange={e => setDims(d => { const arr=[...(d.windowOpenings||[])]; arr[i]={...arr[i],h:e.target.value}; return {...d,windowOpenings:arr}; })}
                              style={{ ...inputStyle, fontSize: '14px' }}
                              onFocus={e => { e.target.style.borderColor = BRAND; }}
                              onBlur={e => { e.target.style.borderColor = '#e5e7eb'; }}
                            />
                          </div>
                          <button onClick={() => setDims(d => ({ ...d, windowOpenings: (d.windowOpenings||[]).filter((_,idx)=>idx!==i) }))}
                            style={{ background:'transparent', border:'none', cursor:'pointer', color:'#9ca3af', paddingBottom:6, fontSize:16 }}>✕</button>
                        </div>
                      ))}
                    </div>

                    {/* Кути (тільки plaster/wall_prep) */}
                    {!isCladding && !isMasonry && (
                    <>
                    <div className="text-[11px] font-medium text-gray-500 mb-1.5 mt-2">Кути</div>
                    <div className="flex gap-2 mb-2">
                      <div className="flex-1">
                        <label className="text-[11px] text-gray-400 mb-1 block">Внутрішні кути</label>
                        <input type="number" inputMode="numeric" value={dims.innerCorners}
                          onChange={e => setDims(d => ({ ...d, innerCorners: e.target.value }))}
                          style={{ ...inputStyle, fontSize: '14px', textAlign: 'center' }}
                          onFocus={e => { e.target.style.borderColor = BRAND; }}
                          onBlur={e => { e.target.style.borderColor = '#e5e7eb'; }}
                        />
                      </div>
                      <div className="flex-1">
                        <label className="text-[11px] text-gray-400 mb-1 block">Зовнішні кути</label>
                        <input type="number" inputMode="numeric" value={dims.outerCorners}
                          onChange={e => setDims(d => ({ ...d, outerCorners: e.target.value }))}
                          style={{ ...inputStyle, fontSize: '14px', textAlign: 'center' }}
                          onFocus={e => { e.target.style.borderColor = BRAND; }}
                          onBlur={e => { e.target.style.borderColor = '#e5e7eb'; }}
                        />
                      </div>
                      <div className="flex-1">
                        <label className="text-[11px] text-gray-400 mb-1 block">Глибина відкосів, м</label>
                        <input type="number" inputMode="decimal" value={dims.openingDepth}
                          onChange={e => setDims(d => ({ ...d, openingDepth: e.target.value }))}
                          style={{ ...inputStyle, fontSize: '14px' }}
                          onFocus={e => { e.target.style.borderColor = BRAND; }}
                          onBlur={e => { e.target.style.borderColor = '#e5e7eb'; }}
                        />
                      </div>
                    </div>
                    </>
                    )}

                    {/* Армування (тільки masonry газоблок) */}
                    {isMasonry && (selectedTech?.tags || []).includes('msn_gas') && (
                      <div className="mb-2 mt-2">
                        <div className="text-[11px] text-gray-400 mb-1">Армування кожен</div>
                        <div className="flex gap-2">
                          {[{ v: '2', l: 'кожен 2-й ряд' }, { v: '3', l: 'кожен 3-й ряд' }].map(({ v, l }) => (
                            <label key={v} className="flex items-center gap-1.5 text-[12px] cursor-pointer px-3 py-1.5 rounded-lg border"
                              style={{ borderColor: (dims.rebarEvery || '3') === v ? BRAND : '#e5e7eb', background: (dims.rebarEvery || '3') === v ? BRAND + '10' : '#fff', color: (dims.rebarEvery || '3') === v ? BRAND : '#6b7280', fontWeight: (dims.rebarEvery || '3') === v ? 600 : 400 }}>
                              <input type="radio" name="rebarEvery" value={v} checked={(dims.rebarEvery || '3') === v}
                                onChange={() => setDims(d => ({ ...d, rebarEvery: v }))} style={{ display: 'none' }} />
                              {l}
                            </label>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Розчин ready/diy (мурування з розчином) */}
                    {isMasonry && selectedTech && ((selectedTech.tags || []).includes('msn_brick') || (selectedTech.tags || []).includes('msn_slag') || (selectedTech.tags || []).includes('msn_wall')) && (
                      <div className="mb-2 mt-2">
                        <div className="text-[11px] text-gray-400 mb-1">Кладочний розчин</div>
                        <div className="flex gap-2">
                          {[{ v: 'ready', l: 'Готова суміш' }, { v: 'diy', l: 'Приготування' }].map(({ v, l }) => (
                            <label key={v} className="flex items-center gap-1.5 text-[12px] cursor-pointer px-3 py-1.5 rounded-lg border"
                              style={{ borderColor: (dims.mortarMode || 'ready') === v ? BRAND : '#e5e7eb', background: (dims.mortarMode || 'ready') === v ? BRAND + '10' : '#fff', color: (dims.mortarMode || 'ready') === v ? BRAND : '#6b7280', fontWeight: (dims.mortarMode || 'ready') === v ? 600 : 400 }}>
                              <input type="radio" name="mortarMode" value={v} checked={(dims.mortarMode || 'ready') === v}
                                onChange={() => setDims(d => ({ ...d, mortarMode: v }))} style={{ display: 'none' }} />
                              {l}
                            </label>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Тип перемички (masonry, якщо є отвори) */}
                    {isMasonry && ((dims.doorOpenings||[]).length + (dims.windowOpenings||[]).length) > 0 && (
                      <div className="mb-2 mt-2">
                        <div className="text-[11px] text-gray-400 mb-1">Перемичка над отворами</div>
                        <div className="flex gap-2 flex-wrap">
                          {(() => {
                            const tags = selectedTech?.tags || [];
                            const isGas = tags.includes('msn_gas');
                            const opts = isGas
                              ? [{ v: 'aeroc', l: 'Готова Aeroc' }, { v: 'angle', l: 'Кутник' }, { v: 'rebar', l: 'Арматура' }]
                              : [{ v: 'zb', l: 'ЗБ перемичка' }, { v: 'angle', l: 'Кутник' }];
                            const defaultVal = isGas ? 'aeroc' : 'zb';
                            const current = dims.lintelMode || defaultVal;
                            return opts.map(({ v, l }) => (
                              <label key={v} className="flex items-center gap-1.5 text-[12px] cursor-pointer px-3 py-1.5 rounded-lg border"
                                style={{ borderColor: current === v ? BRAND : '#e5e7eb', background: current === v ? BRAND + '10' : '#fff', color: current === v ? BRAND : '#6b7280', fontWeight: current === v ? 600 : 400 }}>
                                <input type="radio" name="lintelMode" value={v} checked={current === v}
                                  onChange={() => setDims(d => ({ ...d, lintelMode: v }))} style={{ display: 'none' }} />
                                {l}
                              </label>
                            ));
                          })()}
                        </div>
                      </div>
                    )}

                    {/* Крок профілю (тільки partition і cladding каркасне) */}
                    {(isPartition || isCladding) && selectedTech && !(selectedTech.tags || []).includes('cl_frameless') && (
                      <div className="mb-2 mt-2">
                        <div className="text-[11px] text-gray-400 mb-1">Крок вертикальних профілів</div>
                        <div className="flex gap-2">
                          {[{ v: '0.6', l: '600 мм (стандарт)' }, { v: '0.4', l: '400 мм (посилений)' }].map(({ v, l }) => (
                            <label key={v} className="flex items-center gap-1.5 text-[12px] cursor-pointer px-3 py-1.5 rounded-lg border"
                              style={{ borderColor: (dims.profileStep || '0.6') === v ? BRAND : '#e5e7eb', background: (dims.profileStep || '0.6') === v ? BRAND + '10' : '#fff', color: (dims.profileStep || '0.6') === v ? BRAND : '#6b7280', fontWeight: (dims.profileStep || '0.6') === v ? 600 : 400 }}>
                              <input type="radio" name="profileStep" value={v} checked={(dims.profileStep || '0.6') === v}
                                onChange={() => setDims(d => ({ ...d, profileStep: v }))}
                                style={{ display: 'none' }}
                              />
                              {l}
                            </label>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Підсумок площі */}
                    {dims.height && dims.wallLength && (() => {
                      const h = parseFloat(dims.height) || 0;
                      const wl = parseFloat(dims.wallLength) || 0;
                      const gross = h * wl;
                      const da = (dims.doorOpenings||[]).reduce((s,o)=>s+(parseFloat(o.w)||0)*(parseFloat(o.h)||0),0);
                      const wa = (dims.windowOpenings||[]).reduce((s,o)=>s+(parseFloat(o.w)||0)*(parseFloat(o.h)||0),0);
                      const net = Math.max(gross - da - wa, 0);
                      return (
                        <div className="mt-2 text-xs text-gray-500 flex flex-wrap gap-x-4 gap-y-1">
                          <span>Загальна: <b>{gross.toFixed(1)} м²</b></span>
                          {(da + wa) > 0 && <span>Отвори: <b>−{(da + wa).toFixed(1)} м²</b></span>}
                          <span>Чиста площа: <b style={{ color: BRAND }}>{net.toFixed(1)} м²</b></span>
                        </div>
                      );
                    })()}

                    {/* Додаткові параметри (тільки wall_prep) */}
                    {selectedWork === 'wall_prep' && (
                      <div className="mt-3">
                        {/* Штроби */}
                        <label className="flex items-center gap-2 text-[12px] text-gray-600 cursor-pointer mb-2">
                          <input type="checkbox" checked={!!dims.hasGrooves}
                            onChange={e => setDims(d => ({ ...d, hasGrooves: e.target.checked }))}
                            style={{ accentColor: BRAND, width: 16, height: 16 }}
                          />
                          Заробка штроб
                        </label>
                        {dims.hasGrooves && (
                          <div className="ml-6 mb-2">
                            <label className="text-[11px] text-gray-400 mb-1 block">Довжина штроб, м.п.</label>
                            <input type="number" inputMode="decimal" value={dims.grooveLength}
                              onChange={e => setDims(d => ({ ...d, grooveLength: e.target.value }))}
                              placeholder="0" style={{ ...inputStyle, fontSize: '14px', width: 120 }}
                              onFocus={e => { e.target.style.borderColor = BRAND; }}
                              onBlur={e => { e.target.style.borderColor = '#e5e7eb'; }}
                            />
                          </div>
                        )}

                        {/* Стик стіна-стеля */}
                        <label className="flex items-center gap-2 text-[12px] text-gray-600 cursor-pointer mb-2">
                          <input type="checkbox" checked={!!dims.ceilingJoint}
                            onChange={e => setDims(d => ({ ...d, ceilingJoint: e.target.checked }))}
                            style={{ accentColor: BRAND, width: 16, height: 16 }}
                          />
                          Армування стику стіна-стеля
                        </label>

                        {/* Тип шліфування */}
                        <div className="mt-2">
                          <div className="text-[11px] text-gray-400 mb-1">Тип шліфування</div>
                          <div className="flex gap-2">
                            {[{ val: 'manual', label: 'Ручне (наждачка)' }, { val: 'giraffe', label: 'Жираф (круги)' }].map(({ val, label }) => (
                              <label key={val} className="flex items-center gap-1.5 text-[12px] cursor-pointer"
                                style={{ color: dims.sandingType === val ? BRAND : '#6b7280', fontWeight: dims.sandingType === val ? 600 : 400 }}>
                                <input type="radio" name="sandingType" value={val}
                                  checked={dims.sandingType === val}
                                  onChange={() => setDims(d => ({ ...d, sandingType: val }))}
                                  style={{ accentColor: BRAND, width: 14, height: 14 }}
                                />
                                {label}
                              </label>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Параметри труби (водяна ТП) */}
              {isWarmWater && (
                <div className="mb-4">
                  <div className="text-[13px] font-semibold mb-2" style={{ color: BRAND }}>Параметри труби</div>
                  <div className="flex gap-2.5">
                    <div className="flex-1">
                      <label className="text-[11px] text-gray-400 mb-1 block">Діаметр труби</label>
                      <div style={{ position: 'relative' }}>
                        <select
                          value={pipeParams.diameter}
                          onChange={e => setPipeParams(p => ({ ...p, diameter: e.target.value }))}
                          style={{ ...inputStyle, fontSize: '16px', appearance: 'none', paddingRight: 32 }}
                        >
                          <option value="16">16 мм</option>
                          <option value="20">20 мм</option>
                        </select>
                        <ChevronDown style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', width: 14, height: 14, color: '#9ca3af', pointerEvents: 'none' }} />
                      </div>
                    </div>
                    <div className="flex-1">
                      <label className="text-[11px] text-gray-400 mb-1 block">Крок укладки</label>
                      <div style={{ position: 'relative' }}>
                        <select
                          value={pipeParams.step}
                          onChange={e => setPipeParams(p => ({ ...p, step: e.target.value }))}
                          style={{ ...inputStyle, fontSize: '16px', appearance: 'none', paddingRight: 32 }}
                        >
                          <option value="100">100 мм</option>
                          <option value="150">150 мм</option>
                          <option value="200">200 мм</option>
                          <option value="250">250 мм</option>
                        </select>
                        <ChevronDown style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', width: 14, height: 14, color: '#9ca3af', pointerEvents: 'none' }} />
                      </div>
                    </div>
                  </div>
                  {result && result.pipeLength > 0 && (
                    <div className="mt-1.5 text-xs text-gray-500">
                      Довжина труби: <b style={{ color: BRAND }}>{result.pipeLength} м.п.</b>
                    </div>
                  )}
                </div>
              )}

              {/* Товщини */}
              {selectedTech.layers.some(l => l.hasThickness) && (
                <div className="mb-4">
                  <div className="text-[13px] font-semibold mb-2" style={{ color: BRAND }}>Товщини шарів</div>
                  <div className="flex flex-col gap-2">
                    {selectedTech.layers.filter(l => l.hasThickness).map(l => (
                      <div key={l.id} className="flex items-center gap-2.5">
                        <span className="text-[13px] text-gray-600 flex-1">{l.name}</span>
                        <div className="relative" style={{ width: 90 }}>
                          <input type="number" inputMode="numeric" value={layerThicknesses[l.id] || ''}
                            onChange={e => setLayerThicknesses(p => ({ ...p, [l.id]: e.target.value }))}
                            style={{ ...inputStyle, padding: '8px 32px 8px 10px', textAlign: 'right', fontSize: '16px' }}
                            onFocus={e => { e.target.style.borderColor = BRAND; }}
                            onBlur={e => { e.target.style.borderColor = '#e5e7eb'; }}
                          />
                          <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-[11px] text-gray-400">мм</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Результат */}
              {result && result.layers.length > 0 && (
                <div>
                  <Section title="Основні матеріали" count={`${result.layers.length} поз.`}
                    hint="Відредагувати список можна буде в замовленні"
                  >
                    {result.layers.filter(layer => {
                      if (layer.hidden) return false;
                      if (layer.minGklLayers) {
                        const gklLayer = result.layers.find(l => l.id === 'gkl');
                        if (gklLayer && (gklLayer.gklLayers || 1) < layer.minGklLayers) return false;
                      }
                      return true;
                    }).map((layer, visIdx) => {
                      const isOptional = layer.optional;
                      const isLayerOn = layer.isEnabled !== false;
                      return (
                      <div key={layer.id + '_' + visIdx} className="px-4 py-2.5"
                        style={{ borderBottom: '1px solid #f3f4f6', opacity: isOptional && !isLayerOn ? 0.45 : 1 }}
                      >
                        <div className="flex items-start gap-2.5">
                          {isOptional ? (
                            <input type="checkbox" checked={isLayerOn}
                              onChange={e => {
                                const newToggles = { ...layerToggles, [layer.id]: e.target.checked };
                                setLayerToggles(newToggles);
                              }}
                              className="mt-1 flex-shrink-0"
                              style={{ accentColor: BRAND, width: 16, height: 16, cursor: 'pointer' }}
                            />
                          ) : (
                            <span className="text-[11px] text-gray-400 mt-0.5 w-4 flex-shrink-0">{visIdx + 1}</span>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="text-[13px] font-medium" style={{ color: '#1f2937' }}>{layer.name}</div>
                            <CustomSelect
                              value={productSelections[layer.id] || layer.product?.id || ''}
                              options={layer.products}
                              onChange={(val) => changeProduct(layer.id, val)}
                            />
                            {layer.hasThickness && layer.thickness && (
                              <div className="text-[10px] text-gray-400 mt-1">товщина {layer.thickness} мм</div>
                            )}
                          </div>
                          <div className="text-right flex-shrink-0 min-w-[80px]">
                            <div className="text-sm font-bold" style={{ color: isOptional && !isLayerOn ? '#9ca3af' : BRAND }}>
                              {isOptional && !isLayerOn ? '—' : fmt(layer.qtyRounded)} <span className="text-[10px] font-normal text-gray-400">{layer.unit}</span>
                            </div>
                            {isLayerOn && layer.packs > 0 && layer.packagingUnit && (
                              <div className="text-[10px] font-semibold mt-0.5" style={{ color: ACCENT }}>
                                {layer.packs} {layer.packagingUnit} × {layer.packagingSize} {layer.unit}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );})}
                  </Section>

                  {result.consumables.length > 0 && (
                    <Section title="Витратні матеріали" count={`${result.consumables.filter((_, i) => !deletedConsumables.includes(i)).length + customConsumables.length} поз.`}>
                      {result.consumables.map((c, i) => {
                        if (deletedConsumables.includes(i)) return null;
                        const editedQty = consumableEdits[i] !== undefined ? consumableEdits[i] : c.qty;
                        return (
                          <div key={i} className="px-4 py-2 flex items-center gap-2"
                            style={{ borderBottom: '1px solid #f3f4f6' }}
                          >
                            <span className="text-[11px] text-gray-400 w-4 flex-shrink-0">{i + 1}</span>
                            <span className="flex-1 text-[13px] text-gray-600 min-w-0">{c.name}</span>
                            <div className="flex items-center gap-1.5 flex-shrink-0">
                              <input
                                type="number" inputMode="numeric"
                                value={editedQty}
                                onChange={e => setConsumableEdits(prev => ({ ...prev, [i]: e.target.value === '' ? '' : Math.max(0, parseInt(e.target.value) || 0) }))}
                                style={{
                                  width: 60, padding: '4px 6px', border: '1.5px solid #e5e7eb',
                                  borderRadius: 8, fontSize: 13, fontWeight: 600, textAlign: 'right',
                                  outline: 'none', fontFamily: 'inherit', background: '#fff', color: BRAND,
                                }}
                                onFocus={e => { e.target.style.borderColor = BRAND; }}
                                onBlur={e => { e.target.style.borderColor = '#e5e7eb'; }}
                              />
                              <span className="text-[10px] text-gray-400 w-7">{c.unit}</span>
                              <button
                                onClick={() => setDeletedConsumables(prev => [...prev, i])}
                                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, opacity: 0.4 }}
                                title="Видалити"
                              >
                                <X style={{ width: 14, height: 14, color: '#9ca3af' }} />
                              </button>
                            </div>
                          </div>
                        );
                      })}

                      {/* Custom consumables */}
                      {customConsumables.map((cc, i) => (
                        <div key={'custom_' + i} className="px-4 py-2 flex items-center gap-2"
                          style={{ borderBottom: '1px solid #f3f4f6' }}
                        >
                          <span className="text-[11px] text-gray-400 w-4 flex-shrink-0">+</span>
                          <span className="flex-1 text-[13px] text-gray-600 min-w-0">{cc.name}</span>
                          <div className="flex items-center gap-1.5 flex-shrink-0">
                            <input
                              type="number" inputMode="numeric"
                              value={cc.qty}
                              onChange={e => {
                                const val = e.target.value === '' ? '' : Math.max(0, parseInt(e.target.value) || 0);
                                setCustomConsumables(prev => prev.map((item, idx) => idx === i ? { ...item, qty: val } : item));
                              }}
                              style={{
                                width: 60, padding: '4px 6px', border: '1.5px solid #e5e7eb',
                                borderRadius: 8, fontSize: 13, fontWeight: 600, textAlign: 'right',
                                outline: 'none', fontFamily: 'inherit', background: '#fff', color: BRAND,
                              }}
                              onFocus={e => { e.target.style.borderColor = BRAND; }}
                              onBlur={e => { e.target.style.borderColor = '#e5e7eb'; }}
                            />
                            <span className="text-[10px] text-gray-400 w-7">{cc.unit}</span>
                            <button
                              onClick={() => setCustomConsumables(prev => prev.filter((_, idx) => idx !== i))}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, opacity: 0.4 }}
                              title="Видалити"
                            >
                              <X style={{ width: 14, height: 14, color: '#9ca3af' }} />
                            </button>
                          </div>
                        </div>
                      ))}

                      {/* Add custom consumable */}
                      {showAddConsumable ? (
                        <div className="px-4 py-3" style={{ background: '#fafaf9' }}>
                          <div className="flex flex-col gap-2">
                            <input
                              type="text" value={newConsumableName}
                              onChange={e => setNewConsumableName(e.target.value)}
                              placeholder="Назва матеріалу"
                              style={{ ...inputStyle, fontSize: 13, padding: '8px 10px' }}
                              onFocus={e => { e.target.style.borderColor = BRAND; }}
                              onBlur={e => { e.target.style.borderColor = '#e5e7eb'; }}
                            />
                            <div className="flex gap-2">
                              <input
                                type="number" inputMode="numeric" value={newConsumableQty}
                                onChange={e => setNewConsumableQty(e.target.value)}
                                placeholder="К-ть"
                                style={{ ...inputStyle, fontSize: 13, padding: '8px 10px', width: 80, flexShrink: 0 }}
                                onFocus={e => { e.target.style.borderColor = BRAND; }}
                                onBlur={e => { e.target.style.borderColor = '#e5e7eb'; }}
                              />
                              <div style={{ position: 'relative', flex: 1 }}>
                                <select
                                  value={newConsumableUnit}
                                  onChange={e => setNewConsumableUnit(e.target.value)}
                                  style={{ ...inputStyle, fontSize: 13, padding: '8px 10px', appearance: 'none', paddingRight: 28 }}
                                >
                                  <option value="шт.">шт.</option>
                                  <option value="м.п.">м.п.</option>
                                  <option value="м²">м²</option>
                                  <option value="кг">кг</option>
                                  <option value="л">л</option>
                                  <option value="уп.">уп.</option>
                                </select>
                                <ChevronDown style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', width: 12, height: 12, color: '#9ca3af', pointerEvents: 'none' }} />
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={() => {
                                  if (!newConsumableName.trim() || !newConsumableQty) return;
                                  setCustomConsumables(prev => [...prev, {
                                    name: newConsumableName.trim(),
                                    qty: parseInt(newConsumableQty) || 0,
                                    unit: newConsumableUnit,
                                  }]);
                                  setNewConsumableName(''); setNewConsumableQty(''); setNewConsumableUnit('шт.');
                                  setShowAddConsumable(false);
                                }}
                                disabled={!newConsumableName.trim() || !newConsumableQty}
                                className="flex-1 py-2 text-white text-xs font-semibold rounded-lg disabled:opacity-30"
                                style={{ background: BRAND }}
                              >Додати</button>
                              <button
                                onClick={() => { setShowAddConsumable(false); setNewConsumableName(''); setNewConsumableQty(''); }}
                                className="py-2 px-4 text-xs font-semibold rounded-lg border"
                                style={{ color: '#6b7280', borderColor: '#e5e7eb', background: '#fff' }}
                              >Скасувати</button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <button
                          onClick={() => setShowAddConsumable(true)}
                          className="w-full py-2.5 text-xs font-medium transition-all"
                          style={{ color: BRAND, background: '#fafaf9', border: 'none', cursor: 'pointer' }}
                        >+ Додати свій матеріал</button>
                      )}
                    </Section>
                  )}


                  <div className="mt-3 mb-1">
                    {isGuest ? (
                      <div style={{ padding: '14px 16px', borderRadius: 12, background: '#f0fdf4', border: '1.5px solid #bbf7d0' }}>
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#14532d', marginBottom: 4 }}>
                          Зберегти розрахунок і сформувати замовлення
                        </div>
                        <div style={{ fontSize: 12, color: '#166534', marginBottom: 10, lineHeight: 1.5 }}>
                          Збережіть список матеріалів і сформуйте замовлення постачальнику
                        </div>
                        <button
                          onClick={onGuestRegister}
                          className="w-full py-2.5 text-white text-[13px] font-semibold rounded-xl"
                          style={{ background: '#16a34a', border: 'none', cursor: 'pointer' }}
                        >
                          Зареєструватись безкоштовно
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={createOrder}
                        disabled={orderCreating || orderCreated}
                        className="w-full py-3 text-white text-[14px] font-semibold rounded-xl transition-all disabled:opacity-70"
                        style={{ background: orderCreated ? '#22c55e' : BRAND }}
                      >
                        {orderCreating ? (
                          <span className="flex items-center justify-center gap-2">
                            <Loader className="w-4 h-4 animate-spin" /> Створення...
                          </span>
                        ) : orderCreated ? (
                          <span className="flex items-center justify-center gap-2">
                            <Check className="w-4 h-4" /> Замовлення створено
                          </span>
                        ) : (
                          'Створити замовлення'
                        )}
                      </button>
                    )}
                  </div>

                  <button onClick={restart}
                    className="w-full mt-2 py-2 text-xs text-gray-400 hover:text-gray-600 transition-colors"
                    style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                  >Новий розрахунок</button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ═══ AI CHAT BAR ═══ */}
        {isGuest ? (
          <div className="flex-shrink-0 border-t" style={{ borderColor: '#e5e7eb', background: '#fafaf9' }}>
            <div style={{ padding: '12px 16px', paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom, 0px))' }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: BRAND, marginBottom: 4 }}>
                AI-підбір технологій та матеріалів
              </div>
              <div style={{ fontSize: 12, color: '#5a7186', marginBottom: 10, lineHeight: 1.5 }}>
                Опишіть задачу — AI підбере оптимальну технологію і відповість на технічні питання
              </div>
              <button
                onClick={onGuestRegister}
                className="w-full py-2.5 text-[13px] font-semibold rounded-xl"
                style={{ background: BRAND, color: '#fff', border: 'none', cursor: 'pointer' }}
              >
                Зареєструватись безкоштовно
              </button>
            </div>
          </div>
        ) : (
          <div className="flex-shrink-0 border-t" style={{ borderColor: '#e5e7eb', background: '#fafaf9' }}>
            {chatMessages.length > 0 && (
              <div className="px-4 pt-2 pb-1 max-h-32 overflow-y-auto">
                {chatMessages.map((msg, i) => (
                  <div key={i} className={`text-xs mb-1.5 ${msg.role === 'user' ? 'text-right' : 'text-left'}`}>
                    <span className={`inline-block px-2.5 py-1.5 rounded-lg max-w-[85%]`}
                      style={{ background: msg.role === 'user' ? '#f3f4f6' : '#fffbeb', color: msg.role === 'user' ? '#374151' : '#92400e' }}
                    >{msg.text}</span>
                  </div>
                ))}
                {chatLoading && (
                  <div className="text-xs text-left mb-1.5">
                    <span className="inline-block px-2.5 py-1.5 rounded-lg" style={{ background: '#fffbeb' }}>
                      <Loader className="w-3.5 h-3.5 animate-spin inline" style={{ color: ACCENT }} />
                    </span>
                  </div>
                )}
                <div ref={chatEndRef} />
              </div>
            )}
            <div className="px-3 py-2 flex items-center gap-2" style={{ paddingBottom: 'calc(0.5rem + env(safe-area-inset-bottom, 0px))' }}>
              <input
                type="text" value={chatInput}
                onChange={e => setChatInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') sendChat(); }}
                placeholder={step === 'work_types' ? "Опишіть задачу — AI підбере вид робіт..." : step === 'calculate' ? "Опишіть розміри — AI заповнить форму..." : "Опишіть задачу — AI підбере технологію..."}
                className="flex-1 px-3 py-2.5 border rounded-xl text-sm focus:outline-none"
                style={{ borderColor: '#e5e7eb', fontSize: '16px', borderWidth: '1.5px', background: '#fff' }}
                onFocus={e => { e.target.style.borderColor = ACCENT; }}
                onBlur={e => { e.target.style.borderColor = '#e5e7eb'; }}
              />
              <button onClick={sendChat} disabled={!chatInput.trim() || chatLoading}
                className="flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center transition-all disabled:opacity-30"
                style={{ background: BRAND, border: 'none', cursor: 'pointer' }}
              >
                <Send className="w-4 h-4 text-white" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MaterialCalculator;
